import React, { useEffect, useState, useRef } from "react";
import HttpService from "../../services/httpService";
import { setLoading, setSnackInfo } from "../../redux/actions/appAction";
import { connect } from "react-redux";
import { FormControl, TextField } from '@material-ui/core';
import { setOverAllDesignSummary } from "../../redux/actions/leadSourceActions";
import { useDispatch } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import MenuItem from '@material-ui/core/MenuItem/MenuItem';
import Autocomplete from '@material-ui/lab/Autocomplete';
import Grid from "@material-ui/core/Grid";
import InfoIcon from '@material-ui/icons/Info';
import SendIcon from '@material-ui/icons/Send';
import Tooltip from '@material-ui/core/Tooltip';
import {
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Paper,
    withStyles,
    Checkbox,
    TableContainer,
    Box,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormLabel,
} from "@material-ui/core";
import { IconButton } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import VisibilityIcon from '@material-ui/icons/Visibility';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@material-ui/core';
import { Button, Divider } from "antd";
import { Modal } from "antd";
import { CSVLink } from "react-csv";
import moment from "moment";
import { ROLENAME, salesDepartment } from '../../utils/index';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom'
import { DownloadOutlined } from '@ant-design/icons';
import CallingDeskDialog, { CallingDeskCallButton, CALLING_DESK_MAX_LEADS } from '../../components/CallingDeskDialog';

const DESIGN_SUMMARY_STAGE_LABELS = {
    designerNotAssignedPendingWithSales: 'Designer Not Assigned Pending With Sales',
    designerNotAssignedKOMRejected: 'Designer Not Assigned KOM Rejected',
    designerNotAssignedPendingWithDesign: 'Designer Not Assigned - Pending with Design',
    designKickOffInternal: 'Design KickOff Internal',
    designKickOffCustomer: 'Design KickOff Customer',
    design: 'Design',
    'design-Hold': 'Design-Hold',
    designHoldGrand: 'Design Hold',
    completed: 'Design Signed Off',
    lost: 'Lost',
};

const getMergedStageLabels = (detailKey) => {
    if (!detailKey || !String(detailKey).includes('/')) return null;
    return String(detailKey)
        .split('/')
        .map((key) => DESIGN_SUMMARY_STAGE_LABELS[key] || key);
};

/** Same palette as the default (non-grouped) Design Summary table. */
const DESIGN_SUMMARY_TABLE = {
    headerBg: '#1C375D',
    headerText: '#FFF2EB',
    cellData: { color: '#1C375D', fontWeight: 'normal' },
    cellDataGrandTotalRow: { color: '#355025', fontWeight: 'bolder' },
    cellGrandTotalColumn: { color: '#004d40', fontWeight: 'bolder' },
};

const designSummaryClickableCellStyle = (colorOpts = {}) => ({
    textAlign: 'center',
    cursor: 'pointer',
    textDecoration: 'underline',
    ...colorOpts,
});

const designSummaryGroupedHeaderStyle = (extra = {}) => ({
    textAlign: 'center',
    backgroundColor: DESIGN_SUMMARY_TABLE.headerBg,
    color: DESIGN_SUMMARY_TABLE.headerText,
    ...extra,
});

const DESIGN_SUMMARY_MONTHS = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const serializeIdListForQuery = (items) => {
    if (items == null || items === '') return '';
    if (!Array.isArray(items)) {
        if (typeof items === 'object' && items._id != null) return String(items._id);
        return String(items);
    }
    return items
        .map((item) => (item != null && typeof item === 'object' ? item._id : item))
        .filter((id) => id != null && id !== '')
        .join(',');
};

/** Same label as default table: Mar-26 (also handles clubbed API month "3/4-2026"). */
const formatDesignSummaryMonthLabel = (month, year) => {
    if (year === 'Grand Total' || month === 'Total') {
        return 'Grand Total';
    }
    if (month === undefined || month === null || year === undefined || year === null) {
        return '';
    }

    const shortYear = (y) => {
        const n = Number(y);
        return Number.isFinite(n) ? n % 100 : String(y).slice(-2);
    };

    if (typeof month === 'string') {
        const clubbed = month.match(/^([\d/]+)-(\d{4})$/);
        if (clubbed) {
            const codes = clubbed[1]
                .split('/')
                .map((c) => parseInt(c, 10))
                .filter((n) => n >= 1 && n <= 12);
            if (codes.length) {
                const names = codes.map((c) => DESIGN_SUMMARY_MONTHS[c]).join('/');
                return `${names}-${shortYear(clubbed[2])}`;
            }
        }
        if (month.includes('/')) {
            const codes = month
                .split('/')
                .map((c) => parseInt(c, 10))
                .filter((n) => n >= 1 && n <= 12);
            if (codes.length) {
                return `${codes.map((c) => DESIGN_SUMMARY_MONTHS[c]).join('/')}-${shortYear(year)}`;
            }
        }
        const parsed = parseInt(month, 10);
        if (!isNaN(parsed) && DESIGN_SUMMARY_MONTHS[parsed]) {
            return `${DESIGN_SUMMARY_MONTHS[parsed]}-${shortYear(year)}`;
        }
    }

    if (typeof month === 'number' && DESIGN_SUMMARY_MONTHS[month]) {
        return `${DESIGN_SUMMARY_MONTHS[month]}-${shortYear(year)}`;
    }

    return '';
};

const formatGroupedColumnTitle = (detailKey, groupName, columnIndex) => {
    const merged = getMergedStageLabels(detailKey);
    if (merged) {
        const base = groupName?.trim() || 'Selected Group';
        return columnIndex > 0 ? `${base} ${columnIndex + 1}` : base;
    }
    if (DESIGN_SUMMARY_STAGE_LABELS[detailKey]) return DESIGN_SUMMARY_STAGE_LABELS[detailKey];
    return String(detailKey)
        .replace('designHoldGrand', 'Design Hold')
        .replace(/^completed$/, 'Design Signed Off')
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (str) => str.toUpperCase());
};

// Who sees which filter. These lists were written out inline - the scope one
// twice - so adding a role meant finding every copy and getting all of them
// right. Named once, they stay in step. Membership below is exactly what was
// on screen before, plus Operation Manager and DGM Marketing, matching the
// same change on the Sales Summary page.
// Note ROLENAME.OperationManager ("Operation Manager") is NOT
// ROLENAME.OperationDesignManager ("Operation Design Manager") - this page
// gates on both, and they are different people.
const FILTER_ALIGN_ROLES = [
    ROLENAME.CenterHead, ROLENAME.BusinessHead, ROLENAME.DesignHead, ROLENAME.DGM_Design, ROLENAME.AGM_Design,
    ROLENAME.Admin, ROLENAME.DataAnalyst, ROLENAME.OperationDesignManager,
    ROLENAME.AssistantDesignManager, ROLENAME.DesignUser, ROLENAME.SalesHead, ROLENAME.DGM_Sales,
    ROLENAME.SalesManager, ROLENAME.AGMSales,
    ROLENAME.OperationManager, ROLENAME.DGM_Marketing,
];
// Location and Exp-Center.
const SCOPE_FILTER_ROLES = [
    ROLENAME.CenterHead, ROLENAME.BusinessHead, ROLENAME.DesignHead, ROLENAME.DGM_Design, ROLENAME.AGM_Design,
    ROLENAME.Admin, ROLENAME.DataAnalyst, ROLENAME.OperationDesignManager,
    ROLENAME.AssistantDesignManager, ROLENAME.DesignUser, ROLENAME.SalesHead, ROLENAME.DGM_Sales,
    ROLENAME.FinanceUser, ROLENAME.FinanceManager, ROLENAME.FinanceExecutive,
    ROLENAME.SalesManager, ROLENAME.AGMSales,
    ROLENAME.OperationManager, ROLENAME.DGM_Marketing,
];
// Select Role + Select Users - the full hierarchy drill-down. The narrower
// gates below it (AGM Design, Sales Manager / Operation Design Manager, the
// assistant managers) are deliberately exclusive and are left alone.
const HIERARCHY_FILTER_ROLES = [
    ROLENAME.CenterHead, ROLENAME.BusinessHead, ROLENAME.DGM_Design, ROLENAME.DesignHead, ROLENAME.Admin,
    ROLENAME.DataAnalyst, ROLENAME.SalesHead, ROLENAME.DGM_Sales,
    ROLENAME.OperationManager, ROLENAME.DGM_Marketing,
];

const hasAnyRole = (userDetails, allowed) => Boolean(
    userDetails?.roles && userDetails.roles.some((role) => allowed.includes(role.name))
);

const useStyles = makeStyles((theme) => ({
    root: {
        width: '100%',
        '& > * + *': {
            marginTop: theme.spacing(2),
        },
    },
    bordercolors: {
        border: '1px solid #eee5e5',
    },
    /** Matches default table: month labels live in navy cells like thead StyledTableCell. */
    groupedStickyMonthCell: {
        position: 'sticky',
        left: 0,
        zIndex: 2,
        textAlign: 'center',
        fontSize: 13,
        fontWeight: 'normal',
        backgroundColor: '#1C375D',
        color: '#FFF2EB',
        boxShadow: '2px 0 3px -1px rgba(0, 0, 0, 0.12)',
    },
    groupedStickyMonthCellGrandTotal: {
        backgroundColor: '#1C375D',
        color: '#FFF2EB',
        fontSize: 15,
        fontWeight: 'bolder',
    },
    groupedFooterRow: {
        '& td:not(:first-child)': {
            backgroundColor: `${theme.palette.background.paper} !important`,
        },
    },
    groupedDataCell: {
        backgroundColor: '#ffffff',
    },
    table: {
        minWidth: 700,
    },
    root: {
        width: '100%',
    },
    title: {
        fontSize: 14,
    },
    customTableContainer: {
        overflowX: "initial"
    },
    pos: {
        marginBottom: 12,
    },
    paper: {
        padding: theme.spacing(2),
        textAlign: 'center',
        color: theme.palette.text.secondary,
    },
    textField: {
        marginLeft: theme.spacing(1),
        marginpivotRight: theme.spacing(1),
        width: 200,
    },
    table: {
        width: "fit-content",
    },
    csv: {
        display: "flex",
        alignItems: "center",
        marginBottom: 20,
    },
    report: {
        marginBottom: 20,
        alignItems: "center",
        justifyContent: "center",
    },
    leadSearchButton: {
        backgroundColor: '#FDB913',
        color: '#151921',
        padding: '1',
        marginRight: '0.5em',
        borderRadius: '20px',
        width: '20%',
        "&:hover": { backgroundColor: "#14419c", color: 'white' }
    },
    leadClearButton: {
        backgroundColor: '#f7b7b7', // light red background
        color: '#151921',
        padding: '1',
        marginRight: '0.5em',
        borderRadius: '20px',
        width: '20%',
        // "&:hover": { 
        //     backgroundColor: "#f7b7b7", // same light red on hover
        //     color: '#151921' // same text color on hover
        // }
    },
    submitButton: {
        backgroundColor: '#F47E46 !important',  // Decorpot Orange
        color: 'white',            // White text
        '&:hover': {
          backgroundColor: '#E0663D !important', // Darker Orange on hover
        },
      },
    columnHeaderInner: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexWrap: 'nowrap',
        whiteSpace: 'nowrap',
        verticalAlign: 'middle',
        gap: 2,
    },
    columnHeaderEye: {
        padding: 2,
        flexShrink: 0,
        position: 'relative',
        zIndex: 12,
    },
}));

const StyledTableCell = withStyles((theme) => ({
    head: {
        backgroundColor: "#1C375D",
        color: '#FFF2EB',
        fontWeight: "500"
    },
    body: {
        fontSize: 14,
    },
}))(TableCell);

const StyledTableRow = withStyles((theme) => ({
    root: {
        "&:nth-of-type(odd)": {
            backgroundColor: theme.palette.action.hover,
        },
    },
}))(TableRow);


// Drill-down status and count field of each stage column in the default table,
// in column order. The Calling Desk (/calling-desk renders this page with the
// `callingDesk` prop) shows a call button in place of each Value column.
const CALLING_DESK_STAGE_COLUMNS = [
    { status: 'designerNotAssignedPendingWithSales', countKey: 'designerNotAssignedPendingWithSalesCount' },
    { status: 'designerNotAssignedKOMRejected', countKey: 'designerNotAssignedKOMRejectedCount' },
    { status: 'designerNotAssignedPendingWithDesign', countKey: 'designerNotAssignedPendingWithDesignCount' },
    { status: 'designKickOffInternal', countKey: 'designKickOffInternalCount' },
    { status: 'designKickOffCustomer', countKey: 'designKickOffCustomerCount' },
    { status: 'design', countKey: 'designCount' },
    { status: 'design-Hold', countKey: 'designHoldcount' },
    { status: 'completed', countKey: 'completedCount' },
    { status: 'lost', countKey: 'lostcount' },
    { status: 'total', countKey: 'totalCount' },
];

const getCallingDeskCountKey = (status) => CALLING_DESK_STAGE_COLUMNS.find((column) => column.status === status)?.countKey;

const DesignSummary = (props) => {
    const classes = useStyles();
    const isCallingDesk = !!props.callingDesk;
    const valueColumnLabel = isCallingDesk ? 'Call' : 'Value';
    const [callingDeskTarget, setCallingDeskTarget] = useState(null);
    const { userDetails } = useSelector((state) => state.user);
    const dispatch = useDispatch();
    const designData = useSelector((state) => state.leadSource.designSummary);
    const [store, setStore] = useState([]);
    const [commonExpCenter, setExpCenter] = useState([])
    const [commonLocation, setLocation] = useState([])
    const [pivot, setPivot] = useState("overallSummary")
    const [isTransposed, setIsTransposed] = useState(false);
    const [storeDateFilter, setPivotDatefilters] = useState({ startDate: "", endDate: "" })
    const [selectedExperienceCenter, setSelectedExperienceCenter] = useState([])
    const [selectedLocation, setSelectedLocation] = useState([])
    const months = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', "Oct", "Nov", "Dec"];
    const [selectedValue, setSelectedValue] = useState("");
    const [showDetailedModal, setShowDetailedModal] = useState(false);
    const [csvData, setCsvData] = useState([]);
    const csvButtonRef = useRef(null);
    const [popMonthData, setPopMonthData] = useState([]);
    const [selectedRole, setSelectedRole] = useState('');
    const [usersData, setUsersData] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [status, setStaus] = useState('');
    const [selectLost, setSeletLost] = useState('')
    const [searchInput, setSearchInput] = useState("")
    const [selectedSearchType, setSelectedSearchType] = useState("");
    const [storeUserGroupDetails, setUserGroupDetails] = useState([])
    const [showNewTable, setShowNewTable] = useState(false); // For toggling the table visibility
    const [groupedTable, setGroupedTable] = useState([])
    const [groups, setGroups] = useState([]);
    const [storeHeadings, setStoreHeadings] = useState([])
    const [storeRowsData, setStoreRowsData] = useState([])
    const searchTypes = ['Customer Name', 'Email ID', 'Lead No', 'Project No', 'Contact No', 'Unique Lead No']
    const [dynamicDataShow, setDynamicDataShow] = useState(0)
    const THIRD_BUTTON_DISABLE_UNTIL_KEY = 'thirdButton_disableUntil';
    const [isDisabled, setIsDisabled] = useState(false);
    const [shouldRefresh, setShouldRefresh] = useState(false);
    const [timeLeft, setTimeLeft] = useState(0);
    const [isSpinning, setIsSpinning] = useState(false);

    const [lastUsedParams, setLastUsedParams] = useState(null);
    const [isInitialRender, setIsInitialRender] = useState(true);
    const [showLatestNotes ,setShowLatestNotes] = useState(false);
    const [latestNotesMsg, setLatestNotesMsg] = useState('');
    const [latestNotesDate, setLatestNotesDate] = useState(null);
    const [showLostNotes, setShowLostNotes] = useState(false);
    const [latestDealMsg, setLatestDealMsg] = useState('');
    const [latestDealDate, setLatestDealDate] = useState(null);
    const [latestDealModal, setLatestDealModal] = useState(false);
    const [lostDealActivity, setLostDealActivity] = useState('NA')
    const [lostDealActivityDate, setLostDealActivityDate] = useState(null);
    const [showNpsFormModal, setShowNpsFormModal] = useState(false);
    const [npsCreatedDate, setNpsCreatedDate] = useState(null);
    const [npsFeedback, setNpsFeedback] = useState('');
    const [npsFormData, setNpsFormData] = useState('');
    const [isManagerInvolved, setIsManagerInvolved] = useState('');
    const [managerSatisfactionForm, setManagerSatisfactionForm] = useState([]);
    const [avgScore, setAvgScore] = useState(0);
    const [tableCsvData, setTableCsvData] = useState([]);
    const tableCsvButtonRef = useRef(null);
    const [normalTableCsvData, setNormalTableCsvData] = useState([]);
    const normalTableCsvButtonRef = useRef(null);
    const [transposeTableCsvData, setTransposeTableCsvData] = useState([]);
    const transposeTableCsvButtonRef = useRef(null);
    const [storeTotalsRow, setStoreTotalsRow] = useState(null);
    const displayDate = (month, year) => formatDesignSummaryMonthLabel(month, year);


    useEffect(() => {
        expCenters();
        locations();
        fetchUsersData();
        if (userDetails.roles.find(role => role.name === ROLENAME.SalesManager || role.name === ROLENAME.AssistantSalesManager || role.name === ROLENAME.SalesUser)) {
            overallsummary()
            setSelectedRole(ROLENAME.SalesUser)
        } else if (userDetails.roles.find(role => role.name === ROLENAME.OperationDesignManager || role.name === ROLENAME.AssistantDesignManager || role.name === ROLENAME.DesignUser)) {
            overallsummary()
            setSelectedRole(ROLENAME.DesignUser)
        } else {
            setSelectedRole("ALL")
            overallsummary();
        }
    }, []);

    const getColor = (num) => {
        switch (num) {
            case 1:
                return '#e72600';
            case 2:
                return '#f96500';
            case 3:
                return '#ffe000';
            case 4:
                return '#30ad00';
            case 5:
                return '#218b00';
            default:
                return 'lightgray';
        }
    };

    const headers = [
        { key: "serialNo", label: "Sl. No." },
        { key: "projectCode", label: "Project No." },
        { key: "leadNumber", label: "Lead No." },
        { key: "clientName", label: "Client Name" },
        { key: "designUser", label: "Design User" },
        { key: "designManager", label: "Design Manager" },
        { key: "designAGM", label: "Design AGM" },
        { key: "designDGM", label: "Design DGM" },
        { key: "staticSalesWonBudget", label: "Saleswon Value" },
        { key: "staticDesignWonBudget", label: "Designwon Value" },
        { key: "designStage", label: "Design Stage" },
        { key: "currentStage", label: "Current Stage" },
        { key: "totalValue", label: "Value (INR)" },
        { key: "designerAssignedDate", label: "Designer Assigned Date" },
        { key: "location", label: "City" },
        { key: "expCtrName", label: "Exp. Ctr Name" },
        { key: "salesWonDate", label: "Sales Won Date" },
        { key: "designKOMRejectedStage", label: "KOM Rejected Date" },
        { key: "designerPendingAssignmentStage", label: "DNA Pending With Design Date" },
        { key: "kickoffInternalStage", label: "KickOff Internal Date" },
        { key: "kickoffCustomerStage", label: "KickOff Customer Date" },
        { key: "designSignOffDate", label: "Design Sign-Off Date" },
        { key: "materialDeliveryDate", label: "Material Delivery Date" },
        { key: "actualCompletionDate", label: "Actual Completion Date" },
        { key: "clientCheckListApprovalDate", label: "Client Checklist Approval Date" },
        { key: "agreementGeneratedDate", label: "Designer DKO Checklist Approval TO ODM request Date" },
        { key: "cxDKOApprovalDate", label: "DKO Checklist. - ODM Approval date" },
        ...( (status === "design-Hold" || status === 'total')
        ? [ { key: "designHoldStatus", label: "Design-Hold Status" }, 
            { key: "designHoldCxApproval", label: "Design-Hold Cx Approval"}]
        : []),
         ...( (status === "completed" || status === 'total')
        ? [ { key: "designNpsScore", label: "Design Nps Score"}, 
            { key: "designNpsFeedback", label: "Design Nps Feedback"}]
        : []),
        { key: "latestNotesMsg", label: "Latest Notes"},
        { key: "latestNotesDate", label: "Latest Notes Date"},
        { key: "latestDealMsg", label: "Latest Deal Activity"},
        { key: "latestDealDate", label: "Latest Deal_Activity Date"},
        ...( (status === "lost" || status === 'total')
        ? [ { key: "lostReason", label: "Lost Reason"},
            { key: "lostDate", label: "Lost Date"}]
        : []),
        { key: 'salesUser', label: 'Sales User' },
        { key: 'salesAssistantManager', label: 'Sales ASM' },
        { key: 'salesManager', label: 'Sales Manager' },
        { key: 'salesAGM', label: 'Sales AGM' },
        { key: 'salesDGM', label: 'Sales DGM' },
    ];

    const tableHeaders = [
        { key: "serialNo", label: "Sl. No." },
        { key: "projectCode", label: "Project No." },
        { key: "leadNumber", label: "Lead No." },
        { key: "clientName", label: "Client Name" },
        { key: "currentStage", label: "Current Stage" },
        { key: "valueInr", label: "Value (INR)" },
        { key: "staticSalesWonBudget", label: "staticSalesWonBudget (INR)" },
        { key: "staticDesignWonBudget", label: "staticDesignWonBudget (INR)" },
        { key: "location", label: "City" },
        { key: "expCtrName", label: "Exp. Ctr. Name" },
        { key: "designExpCtrName", label: "Design Experience Center" },
        { key: "salesWonDate", label: "Sales Won Date" },
        { key: "designSignOffDate", label: "Design Sign-Off Date" },
        { key: "materialDeliveryDate", label: "Material Delivery Date" },
        { key: "actualCompletionDate", label: "Actual Completion Date" },
        { key: "cxChecklistApprovalDate", label: "Cx Checklist Approval Date" },
        { key: "cxDKOApprovalDate", label: "Cx DKO Completion Date" },
        ...(status === "design-Hold" || status === "total" ? [
            { key: "designHoldStatus", label: "Design-Hold Status" },
            { key: "designHoldCxApproval", label: "Design-Hold Customer Approval" },
        ] : []),
        ...(status === "completed" || status === "total" ? [
            { key: "overallDesignNps", label: "Overall Design NPS form/Feedback" },
        ] : []),
        { key: "latestNotes", label: "Latest Notes/Date" },
        { key: "latestDealActivity", label: "Latest Deal Activity" },
        ...(status === "lost" || status === "total" ? [
            { key: "lostReason", label: "Lost Reason/Date" },
        ] : []),
    ];

    const tableCsvDownloadHandler = () => {
        setLoading(true);
        try {
            let csvArray = [];
            for (let i = 0; i < popMonthData.length; i++) {
                const data = popMonthData[i];
                let valueInr = "N/A";
                if (status === "completed") {
                    valueInr = data?.leadId?.staticDesignWonAmount
                        ? (data.leadId.staticDesignWonAmount / 1.18) / 100000
                        : "N/A";
                } else {
                    valueInr = data?.leadId?.staticSalesWonBudget || "N/A";
                }

                let obj = {
                    serialNo: i + 1,
                    projectCode: data?.leadId?.erpProjectNo || "N/A",
                    leadNumber: data?.leadId?.lead_no || "N/A",
                    clientName: data?.leadId?.customerId?.name || "N/A",
                    currentStage: data?.leadId?.currentStage || "N/A",
                    valueInr: valueInr,
                    location: data?.salesExpCenter?.locationId?.name || "N/A",
                    expCtrName: data?.salesExpCenter?.name || "N/A",
                    designExpCtrName: data?.leadId?.designWonExpCtrId?.name || "N/A",
                    salesWonDate: data?.SalesWonDate
                        ? moment(data?.SalesWonDate).format("DD-MM-YYYY")
                        : "-",
                    designSignOffDate: data?.leadId?.customerDesignSignOffDate
                        ? moment(data?.leadId?.customerDesignSignOffDate).format("DD-MM-YYYY")
                        : "-",
                    materialDeliveryDate: data?.leadId?.materialDispatchedDate
                        ? moment(data?.leadId?.materialDispatchedDate).format("DD-MM-YYYY")
                        : "-",
                    actualCompletionDate: data?.leadId?.executionCompletionDate
                        ? moment(data?.leadId?.executionCompletionDate).format("DD-MM-YYYY")
                        : "-",
                    cxChecklistApprovalDate: data?.leadId?.customerApproved?.[0]?.approvedDate
                        ? moment(data?.leadId?.customerApproved[0].approvedDate).format("DD-MM-YYYY")
                        : "NA",
                    cxDKOApprovalDate: data?.leadId?.cxDKOApprovalDate
                        ? moment(data?.leadId?.cxDKOApprovalDate).format("DD-MM-YYYY")
                        : "NA",
                };

                if (status === "design-Hold" || status === "total") {
                    obj.designHoldStatus = data?.leadId?.designStatus || "NA";
                    obj.designHoldCxApproval = data?.leadId?.designHoldStatusApproval?.customerApproval || "NA";
                }

                if (status === "completed" || status === "total") {
                    const npsScore = Array.isArray(data?.npsFormData) && data?.npsFormData.length > 0
                        ? (data.npsFormData.reduce((sum, item) => sum + Number(item?.actionSelected), 0) / data.npsFormData.length).toFixed(2)
                        : "0.00";
                    const npsFeedback = data?.npsFeedback ? sanitizeCsvField(data.npsFeedback) : "NA";
                    obj.overallDesignNps = `Score: ${npsScore}, Feedback: ${npsFeedback}`;
                }

                obj.latestNotes = data?.latestNotesMsg
                    ? `${sanitizeCsvField(data.latestNotesMsg)} (${data.latestNotesDate ? moment(data.latestNotesDate).format("DD-MM-YYYY") : "NA"})`
                    : "NA";
                obj.latestDealActivity = data?.latestDealMsg
                    ? `${sanitizeCsvField(data.latestDealMsg)} (${data.latestDealDate ? moment(data.latestDealDate).format("DD-MM-YYYY") : "NA"})`
                    : "NA";

                if (status === "lost" || status === "total") {
                    obj.lostReason = data?.stage === 'Lost' && data?.lostDealActivity
                        ? `${sanitizeCsvField(data.lostDealActivity)} (${data.lostDate && moment(data.lostDate).isValid() ? moment(data.lostDate).format("DD-MM-YYYY") : "NA"})`
                        : "NA";
                }

                csvArray.push(obj);
            }
            setTableCsvData(csvArray);
            setTimeout(() => {
                tableCsvButtonRef.current.link.click();
            });
        } catch (error) {
            console.log("error", error);
            props?.setSnackInfo(
                error?.response?.data?.message ?? "Something went wrong",
                "error"
            );
        }
        setLoading(false);
    };

    /** Sales & design hierarchy — same source as project log (Lead), journey as fallback. */
    const resolveLeadHierarchyUserName = (row, field) => {
        const leadUser = row?.leadId?.[field];
        const journeyUser = row?.[field];
        if (leadUser?.name) return leadUser.name;
        if (journeyUser?.name) return journeyUser.name;
        return 'NA';
    };

    function sanitizeCsvField(value) {
        if (value === null || value === undefined) return "NA";

        return `"${String(value)
            .replace(/[\r\n]+/g, " ")   // remove all newlines
            .replace(/\t+/g, " ")       // remove tabs
            .replace(/,/g, " ")         // replace commas with spaces
            .replace(/;/g, " ")         // replace semicolons with spaces
            .replace(/\s+/g, " ")       // collapse multiple spaces
            .replace(/"/g, '""')        // escape quotes by doubling them
            .trim()}"`;                 // final cleanup
    }
    const csvDownloadHandler = async (data) => {
        setLoading(true);
        try {
            let csvArray = [];
            for (let i = 0; i < popMonthData.length; i++) {
                let obj = {
                    serialNo: i + 1,
                    projectCode: popMonthData[i]?.leadId?.erpProjectNo || "N/A",
                    leadNumber: popMonthData[i]?.leadId?.lead_no || "N/A",
                    clientName: popMonthData[i]?.leadId?.customerId?.name,
                    designUser: resolveLeadHierarchyUserName(popMonthData[i], 'designUser'),
                    designManager: resolveLeadHierarchyUserName(popMonthData[i], 'designManager'),
                    designAGM: resolveLeadHierarchyUserName(popMonthData[i], 'designAGM'),
                    designDGM: resolveLeadHierarchyUserName(popMonthData[i], 'designDGM'),
                    staticSalesWonBudget: popMonthData[i]?.leadId?.staticSalesWonBudget ?? "N/A",
                    staticDesignWonBudget: popMonthData[i]?.leadId?.staticDesignWonBudget ?? "N/A",
                    totalValue:
                        (status === 'completed'
                            ? (popMonthData[i]?.leadId?.staticDesignWonAmount
                                ? (popMonthData[i].leadId.staticDesignWonAmount / 1.18) / 100000
                                : "N/A")
                            : (popMonthData[i]?.leadId?.staticSalesWonBudget
                                ? popMonthData[i].leadId.staticSalesWonBudget
                                : "N/A")),
                    designerAssignedDate: popMonthData[i]?.leadId?.designerAssignedDate
                                            ? moment(popMonthData[i]?.leadId?.designerAssignedDate).format("DD-MM-YYYY")
                                            : "-",
                    location: popMonthData[i]?.salesExpCenter?.locationId?.name,
                    expCtrName: popMonthData[i]?.salesExpCenter?.name,
                    salesWonDate: popMonthData[i]?.SalesWonDate ? moment(popMonthData[i]?.SalesWonDate).format("DD-MM-YYYY") : "-",
                    designKOMRejectedStage: popMonthData[i]?.designKOMRejectedStage ? moment(popMonthData[i]?.designKOMRejectedStage).format("DD-MM-YYYY") : "-",
                    designerPendingAssignmentStage: popMonthData[i]?.designerPendingAssignmentStage ? moment(popMonthData[i]?.designerPendingAssignmentStage).format("DD-MM-YYYY") : "-",
                    kickoffInternalStage: popMonthData[i]?.kickoffInternalStage ? moment(popMonthData[i]?.kickoffInternalStage).format("DD-MM-YYYY") : "-",
                    kickoffCustomerStage: popMonthData[i]?.kickoffCustomerStage ? moment(popMonthData[i]?.kickoffCustomerStage).format("DD-MM-YYYY") : "-",
                    salesUser: resolveLeadHierarchyUserName(popMonthData[i], 'salesUser'),
                    salesAssistantManager: resolveLeadHierarchyUserName(popMonthData[i], 'salesAssistantManager'),
                    salesManager: resolveLeadHierarchyUserName(popMonthData[i], 'salesManager'),
                    salesAGM: resolveLeadHierarchyUserName(popMonthData[i], 'salesAGM'),
                    salesDGM: resolveLeadHierarchyUserName(popMonthData[i], 'salesDGM'),
                    currentStage: popMonthData[i]?.leadId?.currentStage ? popMonthData[i]?.leadId?.currentStage : 'NA',
                    designStage: popMonthData[i]?.stage ? popMonthData[i]?.stage : "NA",
                    designSignOffDate: popMonthData[i]?.leadId?.customerDesignSignOffDate ? moment(popMonthData[i]?.leadId?.customerDesignSignOffDate).format("DD-MM-YYYY") : "NA",
                    materialDeliveryDate: popMonthData[i]?.leadId?.materialDispatchedDate ? moment(popMonthData[i]?.leadId?.materialDispatchedDate).format("DD-MM-YYYY") : "NA",
                    actualCompletionDate: popMonthData[i]?.leadId?.executionCompletionDate ? moment(popMonthData[i]?.leadId?.executionCompletionDate).format("DD-MM-YYYY") : "NA",
                    clientCheckListApprovalDate :popMonthData[i]?.leadId?.customerApproved?.[0]?.status === "Approved" && popMonthData[i]?.leadId?.customerApproved?.[0]?.isApproved ? moment(popMonthData[i]?.leadId?.customerApproved?.[0]?.approvedDate).format("DD-MM-YYYY"): "NA",
                    agreementGeneratedDate: popMonthData[i]?.leadId?.agreementGeneratedDate ? moment(popMonthData[i]?.leadId?.agreementGeneratedDate).format("DD-MM-YYYY") : "NA",
                    cxDKOApprovalDate: popMonthData[i]?.leadId?.cxDKOApprovalDate ? moment(popMonthData[i]?.leadId?.cxDKOApprovalDate).format("DD-MM-YYYY") : "NA",
                    ...(status === 'design-Hold' || status === 'total'
                    ? {
                        designHoldStatus: popMonthData[i]?.leadId?.designStatus || 'NA',
                        designHoldCxApproval: popMonthData[i]?.leadId?.designHoldStatusApproval?.customerApproval || 'NA'
                    }
                    : {}),
                    ...(status === 'completed' || status === 'total'
                    ? {
                        designNpsScore: Array.isArray(popMonthData[i]?.npsFormData) && popMonthData[i]?.npsFormData.length > 0
                        ? (popMonthData[i].npsFormData.reduce((sum, item) => sum + Number(item?.actionSelected), 0) / popMonthData[i].npsFormData.length).toFixed(2)
                        : "0.00",
                        designNpsFeedback: popMonthData[i]?.npsFeedback
                        ? sanitizeCsvField(popMonthData[i].npsFeedback)
                        : "NA",
                    }
                    : {}),
                    latestNotesMsg: popMonthData[i]?.latestNotesMsg ? sanitizeCsvField(popMonthData[i].latestNotesMsg): "NA",
                    latestNotesDate: popMonthData[i]?.latestNotesDate ? moment(popMonthData[i]?.latestNotesDate).format("DD-MM-YYYY"): "NA",
                    latestDealMsg: popMonthData[i]?.latestDealMsg ? sanitizeCsvField(popMonthData[i].latestDealMsg) : "NA",
                    latestDealDate: popMonthData[i]?.latestDealDate ? moment(popMonthData[i]?.latestDealDate).format("DD-MM-YYYY"): "NA",
                    ...(status === 'lost' || status === 'total'
                        ? {
                            lostReason: popMonthData[i]?.stage === 'Lost' && popMonthData[i]?.lostDealActivity
                            ?  sanitizeCsvField(popMonthData[i].lostDealActivity)
                            : "NA",                 
                            lostDate: popMonthData[i]?.stage === 'Lost' ? (moment(popMonthData[i]?.lostDate).isValid() ? moment(popMonthData[i].lostDate).format("DD-MM-YYYY"): 'NA') : 'NA'
                        }
                    : {}),
                };
                csvArray.push(obj);
            }
            setCsvData(csvArray);
            downloadExcel();
        } catch (error) {
            console.error(error);
            props?.setSnackInfo(
                error?.response?.data?.message ?? "Something went wrong",
                "error"
            );
        }
        setLoading(false);
    };

    const downloadExcel = () => {
        setLoading(true);
        try {
            setTimeout(() => {
                csvButtonRef.current.link.click();
            });
        } catch (err) {
            console.error(err.message);
        }
        setLoading(false);
    };
    const expCenterData = {};
    store.forEach((el) => {
        if (!expCenterData[el.experienceCenterName]) {
            expCenterData[el.experienceCenterName] = [];
        }
        expCenterData[el.experienceCenterName].push(el);
    });
    const uniqueexperienceCenterCode = Array.from(new Set(store.map((el) => el.expCenterCode)));
    let uniqueexperienceCenterNames = Array.from(new Set(store.map((el) => el.experienceCenterName)));


    const expCenters = async () => {
        props.setLoading(true);
        try {
            let response = await HttpService.getExpericenCenters();
            const matchedUsers = response.data.filter(user => {
                return userDetails.experienceCenterId.includes(user._id);
            });
            setExpCenter(matchedUsers)
        } catch (error) {
            console.error("Error fetching experience centers:", error);
            props.setSnackInfo(error?.response?.data ?? "Something Went Wrong", "error")
        }
        props.setLoading(false);
    }


    const locations = async () => {
        props.setLoading(true);
        try {
            let response = await HttpService.getLocation();
            const matchedUsers = response.data.filter(user => {
                return userDetails.selectedLocation.includes(user._id);
            });
            setLocation(matchedUsers)
        } catch (error) {
            console.error("Error fetching experience centers:", error);
            props.setSnackInfo(error?.response?.data ?? "Something Went Wrong", "error")
        }
        props.setLoading(false);
    }

    const isEqual = (obj1, obj2) => {
        // First check if both objects have the same keys
        const keys1 = Object.keys(obj1);
        const keys2 = Object.keys(obj2);

        if (keys1.length !== keys2.length) {
            return false;
        }

        // Check if all keys in obj2 exist in obj1
        for (const key of keys2) {
            if (!keys1.includes(key)) {
                return false;
            }
        }

        // Then compare each key's value
        for (const key in obj1) {
            if (Array.isArray(obj1[key])) {
                // For arrays, check if they have the same length and same items
                if (!Array.isArray(obj2[key]) || obj1[key].length !== obj2[key].length) {
                    return false;
                }

                // For arrays of objects with _id, compare by _id
                if (obj1[key].length > 0 && obj1[key][0]?._id) {
                    const obj1Ids = new Set(obj1[key].map(item => item._id));
                    const obj2Ids = new Set(obj2[key].map(item => item._id));

                    // Check if sets are equal
                    if (obj1Ids.size !== obj2Ids.size) {
                        return false;
                    }

                    for (const id of obj1Ids) {
                        if (!obj2Ids.has(id)) {
                            return false;
                        }
                    }
                } else {
                    // Simple comparison for primitive arrays
                    for (let i = 0; i < obj1[key].length; i++) {
                        if (obj1[key][i] !== obj2[key][i]) {
                            return false;
                        }
                    }
                }
            } else if (obj1[key] !== obj2[key]) {
                return false;
            }
        }

        return true;
    };

    const overallsummary = async (addLocation, addExpCenter, usersData, selectedSearchType, searchInput) => {
        let locationData = addLocation?.map((el) => el._id)
        let expCenterData = addExpCenter?.map((el) => el._id)
        let users = ''
        if ((usersData?.users && usersData?.users.length !== 0) || (selectedUsers && selectedUsers.length !== 0)) {
            users = usersData?.users?.map((el) => el._id) || selectedUsers?.map((el) => el._id)
        }
        let obj = {
            startDate: storeDateFilter.startDate,
            endDate: storeDateFilter.endDate,
            selectedLocation: locationData ? locationData : "",
            selectedExperienceCenter: expCenterData ? expCenterData : "",
            tokenPercentage: selectedValue !== "" ? selectedValue : "",
            users: users ? users : "",
            // selectedRole: usersData?.role ? usersData?.role : ROLENAME.DesignUser,
            userExpCenter: userDetails.experienceCenterId,
            userLocation: userDetails.selectedLocation,
            searchType: selectedSearchType ? selectedSearchType : "",
            search: searchInput ? searchInput.toUpperCase() : ""
        }
        if (userDetails.roles.find(role => role.name === ROLENAME.OperationDesignManager || role.name === ROLENAME.AssistantDesignManager || role.name === ROLENAME.DesignUser)) {
            obj.selectedRole = usersData?.role ? usersData?.role : ROLENAME.DesignUser
        } else if (userDetails.roles.find(role => role.name === ROLENAME.SalesManager || role.name === ROLENAME.AssistantSalesManager || role.name === ROLENAME.SalesUser)) {
            obj.selectedRole = usersData?.role ? usersData?.role : ROLENAME.SalesUser
        } else {
            obj.selectedRole = usersData?.role ? usersData?.role : (selectedRole ? selectedRole : "ALL")
        }
        // Determine if we need to make API call
        let shouldMakeApiCall = false;
          
        // Case 1: No salesData - must call API. The Calling Desk always asks the
        // server: the saved summary can be an earlier login's (another user's).
        if (isCallingDesk || !designData ||  designData === null) {
            shouldMakeApiCall = true;
        } 
        // Case 2: Not initial render AND parameters changed
        else if (!isInitialRender && lastUsedParams && !isEqual(lastUsedParams, obj)) {
            shouldMakeApiCall = true;
        }

        props.setLoading(true);
        try {
            // setTimeout(async()=>{
            //     console.log("design ->", designData)
            //     if(designData === null) {
            //         let response = await HttpService.overallSummaryDashBoard(obj);
            //         setStore(response.data.data);
            //         dispatch(setOverAllDesignSummary(response.data.data));
            //         }else{
            //             setStore(designData)
            //         }
            // },600)
          
            if (shouldMakeApiCall) {
                // Call API with current parameters
                let response = await HttpService.overallSummaryDashBoard(obj);
                setStore(response.data.data);
                dispatch(setOverAllDesignSummary(response.data.data));
            } else {
                // Use existing data
                setStore(designData);
            }
            
            // Always save the current parameters
            setLastUsedParams({...obj});
            
            // No longer initial render after first execution
            if (isInitialRender) {
                setIsInitialRender(false);
            }
        } catch (error) {
            props.setSnackInfo(error?.response?.data ?? "Something Went Wrong", "error")
        }
        props.setLoading(false);
    }


    // const checkPivot = async (value) => {
    //     setPivot(value)
    //     if (value === 'value' || value === 'total') {
    //         overallsummary()
    //         setSelectedExperienceCenter([])
    //         setSelectedUsers([])
    //         setSelectedRole("ALL")
    //         handleSelectRoles(null, ROLENAME.SalesUser)
    //     } else if (value === "overallReview") {
    //         overallReview();
    //         setSelectedExperienceCenter([])
    //         setSelectedUsers([])
    //         setSelectedRole(ROLENAME.DesignUser)
    //         handleSelectRoles(null, ROLENAME.DesignUser)
    //     } else if (value === "overallsummary") {
    //         overallsummary()
    //         setSelectedExperienceCenter([])
    //         setSelectedUsers([])
    //         setSelectedRole("ALL")
    //         handleSelectRoles(null, ROLENAME.DesignUser)
    //     }
    //     else if (value === 'lost') {
    //         lastDataAnalaysis()
    //         setSelectedExperienceCenter([])

    //     } else if (value === 'salesOverallReview') {
    //         salesOverallReview()
    //         setSelectedExperienceCenter([])
    //         setSelectedUsers([])
    //         setSelectedRole(ROLENAME.SalesUser)
    //         handleSelectRoles(null, ROLENAME.SalesUser)
    //     } else if (value === 'designSignOffReview') {
    //         designSignOffReview()
    //         setSelectedExperienceCenter([])
    //     }
    // }

    const formatNumberToTwoDecimalPlaces = (value) => {
        return parseFloat(value).toFixed(2);
    }

        // Function to generate CSV data for Normal table (months as rows, metrics as columns)
        const generateNormalTableCsv = () => {
            try {
                if (!store || store.length === 0) {
                    setNormalTableCsvData([]);
                    return;
                }
    
                const csvRows = store.map((ele, index) => {
                    const row = {};
                    
                    // Month And Year
                    row["Month And Year"] = ele?._id?.year === 'Grand Total' || index === store.length - 1
                        ? (ele?._id?.year || 'Grand Total')
                        : `${months[ele?.month]}-${ele?._id?.year % 100}`;
    
                    // Designer Not Assigned Pending With Sales
                    row["Designer Not Assigned Pending With Sales - Count"] = ele.designerNotAssignedPendingWithSalesCount || 0;
                    row["Designer Not Assigned Pending With Sales - Value"] = formatNumberToTwoDecimalPlaces(ele.designerNotAssignedPendingWithSales || 0);
    
                    // Designer Not Assigned KOM Rejected
                    row["Designer Not Assigned KOM Rejected - Count"] = ele.designerNotAssignedKOMRejectedCount || 0;
                    row["Designer Not Assigned KOM Rejected - Value"] = formatNumberToTwoDecimalPlaces(ele.designerNotAssignedKOMRejected || 0);
    
                    // Designer Not Assigned - Pending with Design
                    row["Designer Not Assigned - Pending with Design - Count"] = ele.designerNotAssignedPendingWithDesignCount || 0;
                    row["Designer Not Assigned - Pending with Design - Value"] = formatNumberToTwoDecimalPlaces(ele.designerNotAssignedPendingWithDesign || 0);
    
                    // Design KickOff Internal
                    row["Design KickOff Internal - Count"] = ele.designKickOffInternalCount || 0;
                    row["Design KickOff Internal - Value"] = formatNumberToTwoDecimalPlaces(ele.designKickOffInternal || 0);
    
                    // Design KickOff Customer
                    row["Design KickOff Customer - Count"] = ele.designKickOffCustomerCount || 0;
                    row["Design KickOff Customer - Value"] = formatNumberToTwoDecimalPlaces(ele.designKickOffCustomer || 0);
    
                    // Design
                    row["Design - Count"] = ele.designCount || 0;
                    row["Design - Value"] = formatNumberToTwoDecimalPlaces(ele.design || 0);
    
                    // Design-Hold
                    row["Design-Hold - Count"] = ele.designHoldcount || 0;
                    row["Design-Hold - Value"] = formatNumberToTwoDecimalPlaces(ele.designHoldGrand || 0);
    
                    // Completed
                    row["Design Signed Off - Count"] = ele.completedCount || 0;
                    row["Design Signed Off - Value"] = formatNumberToTwoDecimalPlaces(ele.completed || 0);
    
                    // Lost
                    row["Lost - Count"] = ele.lostcount || 0;
                    row["Lost - Value"] = formatNumberToTwoDecimalPlaces(ele.lost || 0);
    
                    // Grand Total
                    row["Grand Total - Count"] = ele?.totalCount || 0;
                    row["Grand Total - Value"] = formatNumberToTwoDecimalPlaces(ele?.grandTotal || 0);
    
                    return row;
                });
    
                setNormalTableCsvData(csvRows);
            } catch (error) {
                console.error("Error generating normal table CSV:", error);
                props?.setSnackInfo("Error generating normal table CSV", "error");
            }
        };
    
        // Function to generate CSV data for Transpose table (metrics as rows, months as columns)
        const generateTransposeTableCsv = () => {
            try {
                if (!store || store.length === 0) {
                    setTransposeTableCsvData([]);
                    return;
                }
    
                const metrics = [
                    { dataKey: 'designerNotAssignedPendingWithSalesCount', metricKey: 'designerNotAssignedPendingWithSales', label: 'Designer Not Assigned Pending With Sales - Count' },
                    { dataKey: 'designerNotAssignedPendingWithSales', metricKey: 'designerNotAssignedPendingWithSales', label: 'Designer Not Assigned Pending With Sales - Value' },
                    { dataKey: 'designerNotAssignedKOMRejectedCount', metricKey: 'designerNotAssignedKOMRejected', label: 'Designer Not Assigned KOM Rejected - Count' },
                    { dataKey: 'designerNotAssignedKOMRejected', metricKey: 'designerNotAssignedKOMRejected', label: 'Designer Not Assigned KOM Rejected - Value' },
                    { dataKey: 'designerNotAssignedPendingWithDesignCount', metricKey: 'designerNotAssignedPendingWithDesign', label: 'Designer Not Assigned - Pending with Design - Count' },
                    { dataKey: 'designerNotAssignedPendingWithDesign', metricKey: 'designerNotAssignedPendingWithDesign', label: 'Designer Not Assigned - Pending with Design - Value' },
                    { dataKey: 'designKickOffInternalCount', metricKey: 'designKickOffInternal', label: 'Design KickOff Internal - Count' },
                    { dataKey: 'designKickOffInternal', metricKey: 'designKickOffInternal', label: 'Design KickOff Internal - Value' },
                    { dataKey: 'designKickOffCustomerCount', metricKey: 'designKickOffCustomer', label: 'Design KickOff Customer - Count' },
                    { dataKey: 'designKickOffCustomer', metricKey: 'designKickOffCustomer', label: 'Design KickOff Customer - Value' },
                    { dataKey: 'designCount', metricKey: 'design', label: 'Design - Count' },
                    { dataKey: 'design', metricKey: 'design', label: 'Design - Value' },
                    { dataKey: 'designHoldcount', metricKey: 'design-Hold', label: 'Design-Hold - Count' },
                    { dataKey: 'designHoldGrand', metricKey: 'design-Hold', label: 'Design-Hold - Value' },
                    { dataKey: 'completedCount', metricKey: 'completed', label: 'Design Signed Off - Count' },
                    { dataKey: 'completed', metricKey: 'completed', label: 'Design Signed Off - Value' },
                    { dataKey: 'lostcount', metricKey: 'lost', label: 'Lost - Count' },
                    { dataKey: 'lost', metricKey: 'lost', label: 'Lost - Value' },
                    { dataKey: 'totalCount', metricKey: 'total', label: 'Grand Total - Count' },
                    { dataKey: 'grandTotal', metricKey: 'total', label: 'Grand Total - Value' }
                ];
    
                const csvRows = metrics.map((row) => {
                    const csvRow = {};
                    csvRow["Metric"] = row.label;
    
                    // Add data for each month (excluding last one which is Grand Total)
                    store.slice(0, -1).forEach((ele) => {
                        const monthKey = `${months[ele?.month]}-${ele?._id?.year % 100}`;
                        if (ele && ele[row.dataKey] !== undefined) {
                            csvRow[monthKey] = row.dataKey.includes('Count') || row.dataKey === 'lostcount' || row.dataKey === 'designHoldcount' || row.dataKey === 'totalCount'
                                ? (ele[row.dataKey] || 0)
                                : formatNumberToTwoDecimalPlaces(ele[row.dataKey] || 0);
                        } else {
                            csvRow[monthKey] = 0;
                        }
                    });
    
                    // Grand Total column
                    if (store[store.length - 1] && store[store.length - 1][row.dataKey] !== undefined) {
                        csvRow["Grand Total"] = row.dataKey.includes('Count') || row.dataKey === 'lostcount' || row.dataKey === 'designHoldcount' || row.dataKey === 'totalCount'
                            ? (store[store.length - 1][row.dataKey] || 0)
                            : formatNumberToTwoDecimalPlaces(store[store.length - 1][row.dataKey] || 0);
                    } else {
                        csvRow["Grand Total"] = 0;
                    }
    
                    return csvRow;
                });
    
                setTransposeTableCsvData(csvRows);
            } catch (error) {
                console.error("Error generating transpose table CSV:", error);
                props?.setSnackInfo("Error generating transpose table CSV", "error");
            }
        };
    
        // Generate CSV data when store changes - always generate both
        useEffect(() => {
            if (store && store.length > 0) {
                generateNormalTableCsv();
                generateTransposeTableCsv();
            }
        }, [store]);

    const storePivotDate = (e) => {
        const { name, value } = e.target
        setPivotDatefilters({ ...storeDateFilter, [name]: value })
    }
    const handlePivotDateFilter = async () => {
        if (dynamicDataShow !== 0) {
            await overallSummaryDashBoardGroupWise(selectedLocation, selectedExperienceCenter, selectedUsers, selectedSearchType, searchInput, selectedGroup)
        } else {
            overallsummary(selectedLocation, selectedExperienceCenter, selectedUsers, selectedSearchType, searchInput)
        }

    }

    const handleSelectChange = async (event) => {
        if (dynamicDataShow !== 0) {
            setSelectedValue(event.target.value);
            let locationData = selectedLocation?.map((el) => el._id)
            let expCenterData = selectedExperienceCenter?.map((el) => el._id)
            let obj = {
                startDate: storeDateFilter.startDate,
                endDate: storeDateFilter.endDate,
                selectedLocation: locationData ? locationData : "",
                selectedExperienceCenter: expCenterData ? expCenterData : "",
                userLocation: userDetails.selectedLocation,
                userExpCenter: userDetails.selectedExpCenter,
                tokenPercentage: event.target.value !== "" ? event.target.value : "",
                lost: selectLost !== "" ? selectLost : "",
                groupId: selectedGroup
            }
            props.setLoading(true);
            try {
                let users = ''
                if (selectedUsers && selectedUsers.length !== 0) {
                    users = selectedUsers?.map((el) => el._id)
                }
                obj.users = users ? users : ""
                obj.selectedRole = selectedRole
                let response = await HttpService.overallSummaryDashBoardGroupWise(obj);
    
                const organizedData = response.data.organizedData.data;
    
                // Filter out totals row from organizedData
                const regularData = organizedData.filter(item => 
                    item.year !== 'Grand Total' && 
                    item.month !== 'Total' && 
                    item._id !== 'Grand Total' &&
                    !item.isTotalRow
                );
                
                // Get totals row if exists (only one)
                const totalsRowFromApi = organizedData.find(item => 
                    item.year === 'Grand Total' || 
                    item.month === 'Total' || 
                    item._id === 'Grand Total' ||
                    item.isTotalRow === true
                );
    
                const processedData = regularData.map(item => {
                    const { totalCount, grandTotal, month, year, details, selectedRows, detailsArray } = item;
    
                    const uniqueHeadingsSet = new Set();
                    if (detailsArray && detailsArray.length > 0) {
                        detailsArray.forEach(detail => {
                            Object.keys(detail).forEach(detailKey => {
                                if (detailKey.includes('/')) {
                                    uniqueHeadingsSet.add(detailKey);
                                }
                            });
                        });
                    }
    
                    const filteredHeadings = Array.from(uniqueHeadingsSet);
                    setStoreHeadings((prevStoreHeadings) => {
                        const newUniqueHeadings = new Set([...prevStoreHeadings, ...filteredHeadings]);
                        return Array.from(newUniqueHeadings);
                    });
    
                    if (selectedRows && selectedRows.length > 0) {
                        const concatenatedRows = selectedRows.join('/');
                        setStoreRowsData(prevStored => {
                            if (!prevStored.includes(concatenatedRows)) {
                                return [...prevStored, concatenatedRows];
                            }
                            return prevStored;
                        });
                    }
    
                    const filteredDetailsArray = detailsArray
                        ? detailsArray.filter(detail => {
                            const detailKey = Object.keys(detail)[0];
                            const countValue = detail[detailKey]?.count;
                            const valueAmount = detail[detailKey]?.value;
                            return countValue !== undefined && valueAmount !== undefined;
                        })
                        : [];
    
                    return {
                        month,
                        year,
                        totalCount,
                        grandTotal,
                        details: details || [],
                        detailsArray: filteredDetailsArray,
                        selectedRows: selectedRows
                    };
                });
    
                setGroupedTable(processedData);
                setStoreTotalsRow(totalsRowFromApi || null); // Store only one totals row
                setShowNewTable(true);
                
            } catch (error) {
                props.setSnackInfo(error?.response?.data ?? "Something Went Wrong", "error")
            }
            props.setLoading(false);
        } else {
            setSelectedValue(event.target.value);
            let locationData = selectedLocation?.map((el) => el._id)
            let expCenterData = selectedExperienceCenter?.map((el) => el._id)
            let obj = {
                startDate: storeDateFilter.startDate,
                endDate: storeDateFilter.endDate,
                selectedLocation: locationData ? locationData : "",
                selectedExperienceCenter: expCenterData ? expCenterData : "",
                userExpCenter: userDetails.selectedExpCenter,
                userLocation: userDetails.selectedLocation,
                tokenPercentage: event.target.value !== "" ? event.target.value : "",
                lost: selectLost !== "" ? selectLost : ""
            }
            props.setLoading(true);
            try {
                let users = ''
                if (selectedUsers && selectedUsers.length !== 0) {
                    users = selectedUsers?.map((el) => el._id)
                }
                obj.users = users ? users : ""
                obj.selectedRole = selectedRole
                let response = await HttpService.overallSummaryDashBoard(obj);
                setStore(response.data.data);
            } catch (error) {
                props.setSnackInfo(error?.response?.data ?? "Something Went Wrong", "error")
            }
            props.setLoading(false);
        }
    }

    const handleSearchType = (value) => {
        setSelectedSearchType(value)
        setSearchInput('')
    }

    const handleSearch = async (value) => {
        if (dynamicDataShow !== 0) {
            overallSummaryDashBoardGroupWise(selectedLocation, selectedExperienceCenter, selectedUsers, selectedSearchType, searchInput, selectedGroup)
        } else {
            overallsummary(selectedLocation, selectedExperienceCenter, selectedUsers, selectedSearchType, searchInput)
        }

    }
    const handleKeyDown = (event) => {
        if (event.key === 'Enter' && selectedSearchType && searchInput) {
            handleSearch(searchInput.toUpperCase());
        }
    };
    // const handleSelctLost = async (event) => {
    //     setSeletLost(event.target.value)
    //     let expCenterData = selectedExperienceCenter?.map((el) => el._id)
    //     let obj = {
    //         startDate: storeDateFilter.startDate,
    //         endDate: storeDateFilter.endDate,
    //         selectedExperienceCenter: expCenterData ? expCenterData : "",
    //         tokenPercentage: selectedValue !== "" ? selectedValue : "",
    //         lost: event.target.value !== "" ? event.target.value : ''
    //     }
    //     props.setLoading(true);
    //     if (pivot === 'value' || pivot === 'total') {
    //         try {
    //             let users = ''
    //             if (selectedUsers && selectedUsers.length !== 0) {
    //                 users = selectedUsers?.map((el) => el._id)
    //             }
    //             obj.users = users ? users : ""
    //             obj.selectedRole = selectedRole
    //             obj.pivotValue = 'value'
    //             obj.userExpCenter = userDetails.selectedExpCenter
    //             let response = await HttpService.getSalesDataBasedOnExpCenter(obj);
    //             setStore(response.data);
    //         } catch (error) {
    //             props.setSnackInfo(error?.response?.data ?? "Something Went Wrong", "error")
    //         }
    //     } else if (pivot === 'salesOverallReview') {
    //         try {
    //             let users = ''
    //             if (selectedUsers && selectedUsers.length !== 0) {
    //                 users = selectedUsers?.map((el) => el._id)
    //             }
    //             obj.users = users ? users : ""
    //             obj.selectedRole = selectedRole
    //             let response = await HttpService.getSalesDatoverallReviewDashBoard(obj);
    //             setStore(response.data);
    //         } catch (error) {
    //             props.setSnackInfo(error?.response?.data ?? "Something Went Wrong", "error")
    //         }
    //     }
    //     props.setLoading(false);
    // }



    const handleSelectRoles = async (event, role) => {
        setSelectedUsers([])
        let obj = {}
        if (role) {
            obj.role = role
        } else {
            setSelectedRole(event.target.value)
            obj.role = event.target.value
        }
        obj.pivotValue = pivot
        try {
            let response = await HttpService.getSelectedUsersBasedOnRoles(obj);

            if (dynamicDataShow !== 0) {
                if (!role) {
                    await overallSummaryDashBoardGroupWise(selectedLocation, selectedExperienceCenter, obj, selectedSearchType, searchInput, selectedGroup)
                }
            } else {
                if (!role) {
                    await overallsummary(selectedLocation, selectedExperienceCenter, obj, selectedSearchType, searchInput)
                }
            }

            setUsersData(response.data);
        } catch (error) {
            props.setSnackInfo(error?.response?.data ?? "Something Went Wrong", "error")
        }
    }

    const fetchUsersData = async () => {
        let obj = {}
        if (userDetails.roles.find(role => role.name === ROLENAME.SalesManager || role.name === ROLENAME.AssistantSalesManager || role.name === ROLENAME.SalesUser)) {
            obj.role = ROLENAME.SalesUser
        } else if (userDetails.roles.find(role => role.name === ROLENAME.OperationDesignManager || role.name === ROLENAME.AssistantDesignManager || role.name === ROLENAME.DesignUser)) {
            obj.role = ROLENAME.DesignUser
        } else {
            obj.role = "ALL"
        }
        try {
            let response = await HttpService.getSelectedUsersBasedOnRoles(obj);
            setUsersData(response.data)
        } catch (error) {
            console.log(error, "error")
        }
    }

    let storedd = ''

    const handleMultipleExpData = (e, newvalue) => {
        setSelectedExperienceCenter(newvalue)
        handleExpData(newvalue)
    }

    const handleMultipleLocationData = (e, newValue) => {
        setSelectedLocation(newValue)
        handleLocationData(newValue)
    }
    const handleLocationData = async (newvalue) => {
        props.setLoading(true);

        if (dynamicDataShow !== 0) {
            await overallSummaryDashBoardGroupWise(newvalue, selectedExperienceCenter, selectedUsers, selectedSearchType, searchInput, selectedGroup)
        } else {
            await overallsummary(newvalue, selectedExperienceCenter, selectedUsers, selectedSearchType, searchInput)
        }
        props.setLoading(false);
    }

    const handleMultipleUsers = (e, newvalue) => {
        setSelectedUsers(newvalue)
        handleUsersData(newvalue)
    }

    const handleUsersData = async (newvalue) => {
        props.setLoading(true);
        let obj = {
            users: newvalue,
            role: selectedRole
        }

        if (dynamicDataShow !== 0) {
            await overallSummaryDashBoardGroupWise(selectedLocation, selectedExperienceCenter, obj, selectedSearchType, searchInput, selectedGroup)
        } else {
            await overallsummary(selectedLocation, selectedExperienceCenter, obj, selectedSearchType, searchInput)
        }

        props.setLoading(false);
    }

    // const dataBasedOnClick = async (e, ele, status) => {
    //     setStaus(status)
    //     let expCenterData = selectedExperienceCenter?.map((el) => el._id)
    //     let locationData =
    //         selectedLocation && selectedLocation.length > 0
    //             ? selectedLocation.map((el) => el._id)
    //             : "";
    //     let users = ''
    //     if (selectedUsers && selectedUsers.length !== 0) {
    //         users = selectedUsers?.map((el) => el._id)
    //     }
    //     let obj = {}
    //     if (dynamicDataShow !== 0) {

    //         obj = {
    //             status: status.includes('designHoldGrand')
    //                 ? status.replace('designHoldGrand', 'design-Hold')
    //                 : status.includes('designHold')
    //                     ? status.replace('designHold', 'design-Hold')
    //                     : status,

    //             _id: {
    //                 year: ele.year,
    //                 month: ele?.month
    //             },
    //             selectedRows: ele?.selectedRows,
    //             selectedExperienceCenter: expCenterData.length !== 0 ? expCenterData : "",
    //             selectedLocation: locationData.length !== 0 ? locationData : "",
    //             userExpCenter: userDetails.experienceCenterId,
    //             userLocation: userDetails.selectedLocation,
    //             tokenPercentage: selectedValue !== "" ? selectedValue : "",
    //             users: users ? users : "",
    //             searchType: selectedSearchType ? selectedSearchType : "",
    //             search: searchInput !== "" ? searchInput.toUpperCase() : "",
    //             groupId: selectedGroup
    //         }

    //     } else {
    //         obj = {
    //             status: status,
    //             _id: ele._id,
    //             selectedExperienceCenter: expCenterData.length !== 0 ? expCenterData : "",
    //             selectedLocation: locationData.length !== 0 ? locationData : "",
    //             userExpCenter: userDetails.experienceCenterId,
    //             userLocation: userDetails.selectedLocation,
    //             tokenPercentage: selectedValue !== "" ? selectedValue : "",
    //             users: users ? users : "",
    //             searchType: selectedSearchType ? selectedSearchType : "",
    //             search: searchInput !== "" ? searchInput.toUpperCase() : ""
    //         }
    //     }

    //     props.setLoading(true);
    //     obj.role = selectedRole
    //     if (status === 'total' && ele?._id?.year !== 'Grand Total') {
    //         obj.startDate = ''
    //         obj.endDate = ''
    //     }


    //     if (ele?._id?.year === 'Grand Total' && ele?._id?.month === 'Total') {
    //         obj.startDate = storeDateFilter.startDate
    //         obj.endDate = storeDateFilter.endDate
    //     }

    //     if (ele?.year === 'Grand Total' && ele?.month === 'Total') {
    //         obj.startDate = storeDateFilter.startDate
    //         obj.endDate = storeDateFilter.endDate
    //     }

    //     try {

    //         let response;
    //         if (dynamicDataShow !== 0) {
    //             response = await HttpService.getDataBasedClickOverallSummaryForDynamicDetails(obj);
    //         } else {
    //             response = await HttpService.getDataBasedClickOverallSummary(obj);
    //         }

    //         setPopMonthData(response?.data)
    //         setShowDetailedModal(true)
    //     } catch (error) {
    //         props.setSnackInfo(error?.response?.data ?? "Something Went Wrong", "error")
    //     }
    //     props.setLoading(false);
    // }
    // Same request the Count click sends - the Calling Desk reuses it to find
    // every lead behind a cell before calling them.
    const buildDataBasedOnClickRequest = (ele, status) => {
        const expCenterData = selectedExperienceCenter?.map(el => el._id) || [];
        const locationData = selectedLocation?.map(el => el._id) || [];
        const users = selectedUsers?.map(el => el._id) || [];

        const baseFilters = {
            selectedExperienceCenter: expCenterData,
            selectedLocation: locationData,
            userExpCenter: userDetails.experienceCenterId || [],
            userLocation: userDetails.selectedLocation || [],
            tokenPercentage: selectedValue || undefined,
            users,
            searchType: selectedSearchType || undefined,
            search: searchInput ? searchInput.toUpperCase() : undefined,
            role: selectedRole,
            startDate: storeDateFilter?.startDate || "",
            endDate: storeDateFilter?.endDate || ""
        };

        // Month rows carry year/month on the row and inside _id. Always send
        // both, so the drill-down stays on that month. A missing month makes
        // the API return every month in the range, and a count of 7 becomes
        // a call for everyone in the column.
        const year = ele?.year ?? ele?._id?.year;
        const month = ele?.month ?? ele?._id?.month;
        const rowId = (year != null && year !== '' && month != null && month !== '')
            ? { year, month }
            : ele?._id;

        return dynamicDataShow !== 0
                ? {
                    ...baseFilters,
                    status: !status.includes("/") && status.includes("designHold")
                        ? "design-Hold"
                        : status,
                    _id: rowId,
                    selectedRows: ele?.selectedRows,
                    groupId: selectedGroup || undefined
                }
                : {
                    ...baseFilters,
                    status,
                    _id: rowId
                };
    }

     const dataBasedOnClick = async (e, ele, status) => {
        if (e?.stopPropagation) e.stopPropagation();
        setStaus(status);
        props.setLoading(true);

        const obj = buildDataBasedOnClickRequest(ele, status);
        try {
            const response = await HttpService.getDataBasedClickOverallSummary(obj);

            setPopMonthData(response.data)
            setShowDetailedModal(true)
        } catch (error) {
            props.setSnackInfo(error?.response?.data ?? "Something Went Wrong", "error")
        } finally {
            props.setLoading(false);
        }
    }

    const openCallingDeskForCell = async (e, ele, status, count) => {
        e?.stopPropagation?.();
        if (Number(count) > CALLING_DESK_MAX_LEADS) {
            props.setSnackInfo(`${Number(count)} clients in this cell - at most ${CALLING_DESK_MAX_LEADS} can be called at once. Narrow the filters and try again.`, "warning");
            return;
        }
        props.setLoading(true);
        try {
            const response = await HttpService.getDataBasedClickOverallSummary(buildDataBasedOnClickRequest(ele, status));
            let leadIds = [...new Set((response?.data || []).map((row) => row?.leadId?._id).filter(Boolean))];
            // The number on the Initiate button is this cell's count. Never
            // ring more people than that count, even if the drill-down is wider.
            const expected = Number(count);
            if (expected > 0 && leadIds.length > expected) {
                leadIds = leadIds.slice(0, expected);
            }
            if (!leadIds.length) {
                props.setSnackInfo("No clients to call in this cell", "info");
            } else {
                const year = ele?.year ?? ele?._id?.year;
                const month = ele?.month ?? ele?._id?.month;
                const monthLabel = (year === 'Grand Total' || month === 'Total')
                    ? 'Grand Total'
                    : (month && year ? `${months[month]}-${String(year).slice(-2)}` : '');
                const stageLabels = {
                    designerNotAssignedPendingWithSales: 'Designer Not Assigned Pending With Sales',
                    designerNotAssignedKOMRejected: 'Designer Not Assigned KOM Rejected',
                    designerNotAssignedPendingWithDesign: 'Designer Not Assigned - Pending with Design',
                    designKickOffInternal: 'Design KickOff Internal',
                    designKickOffCustomer: 'Design KickOff Customer',
                    design: 'Design',
                    'design-Hold': 'Design-Hold',
                    completed: 'Design Signed Off',
                    lost: 'Lost',
                    total: 'Grand Total',
                };
                const stageLabel = stageLabels[status] || String(status || '').split('/').join(', ');
                // One client goes straight to the call-type list. More than one
                // asks "schedule call for N clients?" first.
                setCallingDeskTarget({
                    leadIds,
                    confirm: leadIds.length > 1,
                    subtitle: [monthLabel, stageLabel].filter(Boolean).join(' · '),
                });
            }
        } catch (error) {
            props.setSnackInfo(error?.response?.data ?? "Something Went Wrong", "error")
        }
        props.setLoading(false);
    }

    const openCallingDeskForLead = (data) => {
        const leadNo = data?.leadId?.lead_no || data?.leadId?.erpProjectNo;
        const customerName = data?.leadId?.customerId?.name;
        setCallingDeskTarget({
            leadIds: [data?.leadId?._id],
            subtitle: [leadNo && `Lead ${leadNo}`, customerName].filter(Boolean).join(' - '),
        });
    }

    const renderCallingDeskCell = (key, ele, status, count, cellStyle) => (
        <StyledTableCell key={key} className={classes.bordercolors} style={{ textAlign: "center", background: "white", ...cellStyle }}>
            <CallingDeskCallButton
                disabled={!Number(count)}
                title={`Call ${Number(count) || 0} client(s)`}
                onClick={(e) => openCallingDeskForCell(e, ele, status, count)}
            />
        </StyledTableCell>
    );

    const buildDesignSummaryFilterQuery = () => {
        const expCenterData = selectedExperienceCenter?.map((el) => el._id) || [];
        const locationData = selectedLocation?.map((el) => el._id) || [];
        const users = selectedUsers?.map((el) => el._id) || [];

        return new URLSearchParams({
            startDate: storeDateFilter?.startDate || '',
            endDate: storeDateFilter?.endDate || '',
            selectedExperienceCenter: serializeIdListForQuery(expCenterData),
            selectedLocation: serializeIdListForQuery(locationData),
            token: selectedValue || '',
            users: serializeIdListForQuery(users),
            selectedRole: selectedRole || '',
            userExpCenter: serializeIdListForQuery(userDetails?.experienceCenterId),
            userLocation: serializeIdListForQuery(userDetails?.selectedLocation),
            searchType: selectedSearchType || '',
            search: searchInput ? searchInput.toUpperCase() : '',
        }).toString();
    };

    const openDesignSummaryLinkedPage = (path) => (event) => {
        event?.stopPropagation?.();
        event?.preventDefault?.();
        try {
            const url = `${path}?${buildDesignSummaryFilterQuery()}`;
            const opened = window.open(url, '_blank', 'noopener,noreferrer');
            if (!opened) {
                props.setSnackInfo('Pop-up blocked. Opening in this tab.', 'warning');
                window.location.assign(url);
            }
        } catch (error) {
            console.error('Design summary linked page', error);
            props.setSnackInfo('Could not open linked page. Please refresh and try again.', 'error');
        }
    };

    const openDesignStageDaysSummary = openDesignSummaryLinkedPage('/design-stage-days-summary');
    const openDesignKickOffCustomerDaysSummary = openDesignSummaryLinkedPage('/design-kickoff-customer-days-summary');
    const openDesignKickOffInternalDaysSummary = openDesignSummaryLinkedPage('/design-kickoff-internal-days-summary');
    const openDesignHoldAnalysis = openDesignSummaryLinkedPage('/designHold-analysis');
    const openInternalDsoSummary = openDesignSummaryLinkedPage('/internal-dso-summary');
    const openLostDataAnalysis = openDesignSummaryLinkedPage('/lost-data-analysis');

    const renderDesignSummaryEyeButton = (onClick, title, iconColor = '#FFF2EB') => (
        <IconButton
            type="button"
            size="small"
            title={title}
            aria-label={title}
            onClick={(event) => {
                event.stopPropagation();
                event.preventDefault();
                onClick(event);
            }}
            className={classes.columnHeaderEye}
            style={{ color: iconColor }}
        >
            <VisibilityIcon style={{ fontSize: 16, color: iconColor }} />
        </IconButton>
    );

    const renderDesignSummaryColumnHeader = (label, { eyeClick, eyeTitle, headingKey, iconColor } = {}) => (
        <span className={classes.columnHeaderInner}>
            <span>{label}</span>
            {eyeClick && renderDesignSummaryEyeButton(eyeClick, eyeTitle, iconColor)}
            {isGroupingMode && headingKey && (
                <Checkbox
                    size="small"
                    style={{ padding: 4 }}
                    onChange={(e) => handleHeadingSelect(headingKey, e.target.checked)}
                />
            )}
        </span>
    );

    const patchLeadReminderFlags = (leadId, patch) => {
        setPopMonthData((prev) => (prev || []).map((row) => {
            const id = row?.leadId?._id || row?.leadId;
            if (String(id) !== String(leadId)) return row;
            return { ...row, leadId: { ...(row.leadId || {}), ...patch } };
        }));
    };

    const dkoReminderWaiting = (sentAt) => {
        if (!sentAt) return false;
        return Date.now() - new Date(sentAt).getTime() < (3 * 60 * 60 * 1000);
    };

    const sendDkoInternalReminder = async (lead, isReminder) => {
        if (!lead?._id) return;
        const sentAt = lead.designKickOffInternalLastReminderAt || lead.designKickOffInternalDesignerEnabledAt;
        if (dkoReminderWaiting(sentAt)) {
            props.setSnackInfo('It will be active after 3 hours', 'error');
            return;
        }
        props.setLoading(true);
        try {
            await HttpService.enableDesignKickOffInternalForDesigner({
                leadDetails: lead,
                isReminder,
            });
            props.setSnackInfo(isReminder ? 'DKO Internal reminder sent' : 'Designer notified for DKO Internal', 'success');
            const sentNow = new Date();
            patchLeadReminderFlags(lead._id, {
                designKickOffInternalDesignerEnabled: true,
                designKickOffInternalDesignerEnabledAt: lead.designKickOffInternalDesignerEnabledAt || sentNow,
                designKickOffInternalLastReminderAt: sentNow,
            });
        } catch (error) {
            props.setSnackInfo(error?.response?.data?.message || 'Something went wrong', 'error');
        }
        props.setLoading(false);
    };

    const sendDsoCustomerReminder = async (lead) => {
        if (!lead?._id) return;
        props.setLoading(true);
        try {
            await HttpService.sendDesignSignOffCustomerReminder({ leadDetails: lead });
            props.setSnackInfo('Design Sign-Off customer reminder sent', 'success');
            patchLeadReminderFlags(lead._id, { designSignOffCustomerLastReminderAt: new Date() });
        } catch (error) {
            props.setSnackInfo(error?.response?.data?.message || 'Something went wrong', 'error');
        }
        props.setLoading(false);
    };

    const sendDkoCustomerReminder = async (lead) => {
        if (!lead?._id) return;
        if (dkoReminderWaiting(lead.designKickOffCustomerLastReminderAt)) {
            props.setSnackInfo('It will be active after 3 hours', 'error');
            return;
        }
        props.setLoading(true);
        try {
            await HttpService.sendDesignKickOffCustomerReminderForDesigner({ leadDetails: lead });
            props.setSnackInfo('DKO Customer reminder sent to designer', 'success');
            patchLeadReminderFlags(lead._id, { designKickOffCustomerLastReminderAt: new Date() });
        } catch (error) {
            props.setSnackInfo(error?.response?.data?.message || 'Something went wrong', 'error');
        }
        props.setLoading(false);
    };

    const reminderNotifyIcon = (key, title, onClick) => (
        <Tooltip key={key} title={title}>
            <span>
                <IconButton
                    size="small"
                    onClick={onClick}
                    aria-label={title}
                    style={{ color: '#157525', padding: 2 }}
                >
                    <SendIcon style={{ fontSize: 16 }} />
                </IconButton>
            </span>
        </Tooltip>
    );

    const renderLeadReminderActions = (lead) => {
        if (!lead) return null;
        const stage = lead.currentStage;
        const actions = [];

        const internalDocsPending = stage === 'Design Kick-Off : Internal'
            && !lead.designKickOffInternalMomLink
            && lead.designKickOffInternalApprovalStatus !== 'Pending'
            && lead.designKickOffInternalApprovalStatus !== 'Approved';
        if (internalDocsPending) {
            const sentAt = lead.designKickOffInternalLastReminderAt || lead.designKickOffInternalDesignerEnabledAt;
            const waiting = dkoReminderWaiting(sentAt);
            actions.push(reminderNotifyIcon(
                'dko-internal',
                waiting ? 'It will be active after 3 hours' : 'Notified DKO Internal CheckList',
                () => sendDkoInternalReminder(lead, Boolean(sentAt))
            ));
        }

        const customerDkoPending = stage === 'Design Kick-Off : Customer'
            && lead.designKickOffInternalApprovalStatus === 'Approved'
            && !lead.isDesignKickOffCustomerApprovedFromDesign;
        if (customerDkoPending) {
            const waiting = dkoReminderWaiting(lead.designKickOffCustomerLastReminderAt);
            actions.push(reminderNotifyIcon(
                'dko-cust',
                waiting ? 'It will be active after 3 hours' : 'Notified DKO Customer CheckList',
                () => sendDkoCustomerReminder(lead)
            ));
        }

        const dsoPending = ['Design Sign-off', 'Design Sign-off HOLD', 'Internal Design Sign-Off'].includes(stage)
            && !lead.customerDesignSignOffDate;
        if (dsoPending) {
            const last = lead.designSignOffCustomerLastReminderAt;
            const canRemind = !last || moment().diff(moment(last), 'days') >= 1;
            actions.push(reminderNotifyIcon(
                'dso-cust',
                'Reminder DSO Customer',
                () => {
                    if (!canRemind) {
                        props.setSnackInfo('Reminder can be sent only once a day', 'error');
                        return;
                    }
                    sendDsoCustomerReminder(lead);
                }
            ));
        }

        if (!actions.length) return null;
        return <div style={{ display: 'flex', justifyContent: 'center', gap: 2, marginTop: 4 }}>{actions}</div>;
    };

    const [isGroupingMode, setIsGroupingMode] = useState(false);
    const [selectedRows, setSelectedRows] = useState({});
    const [selectedHeadings, setSelectedHeadings] = useState({});
    const [open, setOpen] = useState(false); // For controlling dialog open state
    const [groupName, setGroupName] = useState(''); // To store user name
    const [storeGroupDetails, setStoreGroupDetails] = useState([])

    const [selectedGroup, setSelectedGroup] = useState('');

    const dynamicGroupDetails = async () => {
        try {
            let res = await HttpService.dynamicGroupDetails();

            // Transform the response to get an array of objects with name and _id
            // Transform the response to get an array of objects with name, groupId, and mainId
            const transformedGroups = res.data.grouping.flatMap(grouping =>
                grouping.groups.map(group => ({
                    name: group.groupName,      // Use 'name' for groupName
                    _id: group._id,             // Use '_id' as is for the group ID
                    mainId: grouping._id        // Include the main document ID from the grouping
                }))
            );

            // Set the transformed data to state
            setStoreGroupDetails(transformedGroups);

        } catch (error) {
            props.setSnackInfo(error, "Something went wrong");
        }
    };

    useEffect(() => {
        dynamicGroupDetails();
    }, []);

    const handleToggleGroupingMode = () => {
        setIsGroupingMode(!isGroupingMode);
    };

    const handleClickOpen = () => {
        setOpen(true); // Open the dialog
        dynamicGroupDetails()
    };

    const handleClose = () => {
        setOpen(false); // Close the dialog
    };


    const handleHeadingSelect = (type, isChecked) => {
        setStoreHeadings((prevStoreHeadings) => {
            let updatedHeadings;

            if (isChecked) {
                // Add the new heading, ensuring no leading '/'
                updatedHeadings = prevStoreHeadings ? `${prevStoreHeadings}/${type}` : type;
            } else {
                // Remove the heading if it's unchecked
                const headingsArray = prevStoreHeadings.split('/');
                updatedHeadings = headingsArray
                    .filter(heading => heading !== type) // Remove unchecked heading
                    .join('/'); // Re-join without extra leading '/'
            }

            // Ensure no leading '/' if updatedHeadings is not empty
            return updatedHeadings.startsWith('/') ? updatedHeadings.slice(1) : updatedHeadings;
        });
    };



    const handleHeadingSelectForSubGroups = (type, isChecked) => {
        setSelectedHeadings((prevState) => {
            const updatedHeadings = { ...prevState };
            updatedHeadings[type] = isChecked;

            // Get the list of currently checked headings
            const checkedHeadings = Object.keys(updatedHeadings).filter((key) => updatedHeadings[key]);

            // Separate slash and non-slash headings
            const slashHeadings = checkedHeadings.filter((heading) => heading.includes('/'));
            const nonSlashHeadings = checkedHeadings.filter((heading) => !heading.includes('/'));

            // Initialize a variable to hold the concatenated heading result
            let concatenatedHeading = slashHeadings.length > 0 ? slashHeadings.join('/') : null;

            // Append non-slash headings to concatenated slash heading, if it exists
            if (concatenatedHeading && nonSlashHeadings.length > 0) {
                concatenatedHeading = `${concatenatedHeading}/${nonSlashHeadings.join('/')}`;
            } else if (!concatenatedHeading) {
                // If there are no slash headings, just use the non-slash headings
                concatenatedHeading = nonSlashHeadings.join('/');
            }

            // Prepare the final headings list
            let finalHeadings = [...storeHeadings];

            // Remove any existing slash-based headings from storeHeadings
            checkedHeadings.forEach((heading) => {
                const index = finalHeadings.indexOf(heading);
                if (index !== -1) {
                    finalHeadings.splice(index, 1);
                }
            });

            // Add the concatenated heading if it’s not already in finalHeadings
            if (concatenatedHeading && !finalHeadings.includes(concatenatedHeading)) {
                finalHeadings.push(concatenatedHeading);
            }

            // Handle unchecking logic by removing unchecked headings directly
            if (!isChecked) {
                const index = finalHeadings.indexOf(type);
                if (index !== -1) {
                    finalHeadings.splice(index, 1);
                }
            }

            // Update storeHeadings with the final list
            setStoreHeadings(finalHeadings);
            return updatedHeadings; // Return updated checked state
        });
    };




    const handleRowSelect = (month, year, checked) => {


        // Convert year to last two digits
        const formattedYear = year % 100; // Get last two digits of the year

        setSelectedRows((prev) => {
            const newSelectedRows = { ...prev };

            // Initialize the year if it doesn't exist
            if (!newSelectedRows[formattedYear]) {
                newSelectedRows[formattedYear] = new Set();
            }

            // Add or remove the month based on the `checked` state
            if (checked) {
                newSelectedRows[formattedYear].add(month);
            } else {
                newSelectedRows[formattedYear].delete(month);
                // If no months are selected for the year, remove the year entry
                if (newSelectedRows[formattedYear].size === 0) {
                    delete newSelectedRows[formattedYear];
                }
            }


            return newSelectedRows;
        });
    };

    const handleRowSelected = (inputString, checked) => {


        let formattedMonths = [];
        let newSelectedRows; // Declare the variable here

        // Check for the presence of '-'
        if (inputString.includes('-')) {
            const [monthYear, fullYear] = inputString.split('-'); // Split at the last '-'
            const months = monthYear.split('/'); // Split months by '/'

            // Convert year to last two digits
            const formattedYear = fullYear ? (parseInt(fullYear) % 100).toString().padStart(2, '0') : '';
            formattedMonths = months.map(month => `${month}-${formattedYear}`); // Format months as "month-year"
        } else {
            const [month, year] = inputString.split('/');
            const formattedYear = (parseInt(year) % 100).toString().padStart(2, '0'); // Convert to last two digits
            formattedMonths = [`${month}-${formattedYear}`]; // Format it as "month-year"
        }

        // Update selected rows
        setSelectedRows(prev => {
            newSelectedRows = { ...prev }; // Clone previous state

            // Create a set to store all formatted entries in selectedRows
            const existingSelections = new Set();
            Object.values(prev).forEach(monthSet => {
                monthSet.forEach(month => {
                    existingSelections.add(month);
                });
            });

            // If checked, add the formatted months to selectedRows
            if (checked) {
                const groupKey = formattedMonths.join('/'); // Create a group key

                // Only add the entire group if it's not already present
                if (!existingSelections.has(groupKey)) {
                    const year = formattedMonths[0].split('-')[1]; // Extract year from the first month
                    if (!newSelectedRows[year]) {
                        newSelectedRows[year] = new Set();
                    }
                    newSelectedRows[year].add(groupKey); // Add the group key
                }
            } else {
                // If unchecked, remove the formatted months
                const groupKey = formattedMonths.join('/'); // Create a group key for removal
                const year = formattedMonths[0].split('-')[1]; // Extract year
                newSelectedRows[year]?.delete(groupKey); // Remove the group key

                // If no groups are selected for the year, remove the year entry
                if (newSelectedRows[year] && newSelectedRows[year].size === 0) {
                    delete newSelectedRows[year];
                }
            }


            return newSelectedRows;
        });

        // Update storeRowsData using the new selected rows
        setStoreRowsData(prevStored => {
            // Format the selected rows into a concatenated string
            const newRow = formatSelectedRowsed(newSelectedRows);
            if (newRow) {
                // First, filter out existing entries that are substrings of newRow
                const filteredPrevStored = prevStored.filter(row => !newRow.includes(row));

                // Remove any entries from prevStored that are smaller than newRow
                const finalFilteredPrevStored = filteredPrevStored.filter(row => {
                    const rowParts = row.split('/');
                    return !newRow.split('/').some(newRowPart => rowParts.includes(newRowPart));
                });

                // If newRow is not already present in finalFilteredPrevStored, add it to the list
                if (!finalFilteredPrevStored.includes(newRow)) {
                    return [...finalFilteredPrevStored, newRow]; // Append new concatenated row
                }
            }

            return prevStored; // Return unchanged state if newRow is empty or duplicate
        });

    };

    // Helper to format selected rows as "1-24/2-24/3-24/4-24"
    const formatSelectedRows = (selectedRows) => {

        const formattedArray = [];

        // Iterate through each year in selectedRows
        Object.entries(selectedRows).forEach(([year, months]) => {
            // Format each month for the current year
            months.forEach(month => {
                // Create formatted month-year pair
                formattedArray.push(`${month}-${year}`);
            });
        });

        // Join all formatted strings with a slash
        return formattedArray.join('/'); // Return in the required format
    };

    const formatSelectedRowsed = (selectedRows) => {

        const formattedArray = [];

        // Iterate through each year in selectedRows
        Object.entries(selectedRows).forEach(([year, groups]) => {
            groups.forEach(group => {
                // Directly push the group as is (already formatted)
                formattedArray.push(group);
            });
        });


        // Join all formatted strings with a slash
        return formattedArray.join('/'); // Return in the required format
    };



    const filterUniqueHeadings = (headings) => {
        // Split each heading by '/' into parts
        const splitHeadings = headings.map(heading => heading.split('/'));

        // Filter out headings that are already part of a larger heading
        return splitHeadings
            .filter((heading, index, arr) => {
                // For each heading, check if it is part of any larger heading
                return !arr.some((otherHeading, otherIndex) => {
                    // Ensure we're not comparing the same heading with itself
                    if (otherIndex !== index) {
                        // Check if current heading is part of another heading (larger one)
                        return otherHeading.join('/') !== heading.join('/') && otherHeading.join('/').includes(heading.join('/'));
                    }
                    return false;
                });
            })
            .map(heading => heading.join('/')); // Join back the split headings to get the full headings
    };

    const handleSubmit = async () => {
        // Check if storeHeadings is a single string or an array
        const selectedHeadingsArray = typeof storeHeadings === 'string'
            ? [storeHeadings]  // If single string, wrap it in an array without filtering
            : filterUniqueHeadings(storeHeadings);  // If array, filter it


        // return;

        // Prepare the payload with the modified selected headings
        const selectedData = {
            groups: [
                {
                    groupName: groupName,
                    selectedHeadings: [...new Set(selectedHeadingsArray)], // Ensure uniqueness
                    selectedRows: storeRowsData.length > 0 ? storeRowsData : formatSelectedRows(selectedRows),
                    formCategoryName: 'design-summary'
                },
            ],
        };

        // Send the API request and handle the response
        try {
            const response = await HttpService.dynamicGroupingUserWise(selectedData);

            props.setSnackInfo(response.data.message, "success");
            setGroupName('');
            setSelectedHeadings({});
            setSelectedRows({});
            setIsGroupingMode(false);
            dynamicGroupDetails();
            handleClose(); // Close the dialog after successful API call
        } catch (error) {
            props.setSnackInfo(error?.error ?? "Group Limit Exhausted", "error");
            setGroupName('');
            setSelectedHeadings({});
            setSelectedRows({});
            setIsGroupingMode(false);
            dynamicGroupDetails();
            handleClose();
        }
    };



    const clearDateFilter = async () => {
        setSearchInput("")
        setSelectedSearchType("")
        dispatch(setOverAllDesignSummary(null))
        setShouldRefresh(true) // Trigger the useEffect
        overallsummary(selectedLocation, selectedExperienceCenter, selectedUsers, "", "")
        if (dynamicDataShow !== 0) {
            await overallSummaryDashBoardGroupWise(null, null, null, null, null, selectedGroup)
        }
    }

    useEffect(() => {
        // Check if there's a stored disable time in localStorage
        const disableUntilTimestamp = localStorage.getItem(THIRD_BUTTON_DISABLE_UNTIL_KEY);
        
        if (disableUntilTimestamp) {
            const now = new Date().getTime();
            const disableUntil = parseInt(disableUntilTimestamp, 10);
            
            // If the disable time is in the future, calculate remaining time
            if (disableUntil > now) {
            const remainingTimeInSeconds = Math.ceil((disableUntil - now) / 1000);
            setTimeLeft(remainingTimeInSeconds);
            setIsDisabled(true);
            } else {
            // If the time has passed, clear localStorage
            localStorage.removeItem(THIRD_BUTTON_DISABLE_UNTIL_KEY);
            setIsDisabled(false);
            setTimeLeft(0);
            }
        }
        
        // Set up timer to update remaining time
        let timer;
        if (isDisabled && timeLeft > 0) {
            timer = setInterval(() => {
            setTimeLeft(prevTime => {
                const newTime = prevTime - 1;
                if (newTime <= 0) {
                // When time is up, clear localStorage and enable button
                localStorage.removeItem(THIRD_BUTTON_DISABLE_UNTIL_KEY);
                setIsDisabled(false);
                return 0;
                }
                return newTime;
            });
            }, 1000);
        }
        
        return () => clearInterval(timer);
        }, [isDisabled, timeLeft]);
    
        useEffect(() => {
            if (shouldRefresh && designData === null) {
                overallsummary(selectedLocation, selectedExperienceCenter, selectedUsers, "", "")
                setShouldRefresh(false)
            }
        }, [designData, shouldRefresh])
    
        // Format time for display
        const formatTimeLeft = () => {
        const minutes = Math.floor(timeLeft / 60);
        const seconds = timeLeft % 60;
        return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
        };
    
        // Replace your existing handleRefreshClick with this
        const handleRefreshClick = () => {
        // Calculate the timestamp 4 minutes from now
        const disableUntilTimestamp = new Date().getTime() + (4 * 60 * 1000);
        
        // Store the timestamp in localStorage
        localStorage.setItem(THIRD_BUTTON_DISABLE_UNTIL_KEY, disableUntilTimestamp.toString());
        
        // Disable the button for 4 minutes
        setIsDisabled(true);
        setTimeLeft(10); // 4 minutes in seconds
        
        // Run the original functionality
        setIsSpinning(true);
        clearDateFilter();
        setTimeout(() => {
            setIsSpinning(false);
        }, 1000);
        };
    


    const handleGroupChange = (event, value) => {
        if (value) {

            setSelectedHeadings([])
            setUserGroupDetails(value);
            storeId(value?.mainId); // Pass the group ID directly to storeId
            setSelectedExperienceCenter([])
            setPivotDatefilters({ startDate: "", endDate: "" })
            setSelectedValue("")
            setSelectedUsers([])
            setSelectedSearchType("")
            setSearchInput("")
        }
    };

    let storeGroup = ''
    const storeId = (groupId) => {


        // Pass the groupId to the function that needs it
        storeGroup = groupId;
        setSelectedGroup(storeGroup)

        overallSummaryDashBoardGroupWise(null, null, null, null, null, storeGroup);
    };


    const overallSummaryDashBoardGroupWise = async (addLocation, addExpCenter, usersData, selectedSearchType, searchInput, groupId) => {
        let locationData = addLocation?.map((el) => el._id)
        let expCenterData = addExpCenter?.map((el) => el._id)

        let users = ''
        if ((usersData?.users && usersData?.users.length !== 0) || (selectedUsers && selectedUsers.length !== 0)) {
            users = usersData?.users?.map((el) => el._id) || selectedUsers?.map((el) => el._id)
        }
        let obj = {
            startDate: storeDateFilter.startDate,
            endDate: storeDateFilter.endDate,
            selectedExperienceCenter: expCenterData ? expCenterData : "",
            selectedLocation: locationData ? locationData : "",
            tokenPercentage: selectedValue !== "" ? selectedValue : "",
            users: users ? users : "",
            // selectedRole: usersData?.role ? usersData?.role : ROLENAME.DesignUser,
            userExpCenter: userDetails.experienceCenterId,
            userLocation: userDetails.selectedLocation,
            searchType: selectedSearchType ? selectedSearchType : "",
            search: searchInput ? searchInput.toUpperCase() : "",
            groupId: groupId
        }
        if (userDetails.roles.find(role => role.name === ROLENAME.OperationDesignManager || role.name === ROLENAME.AssistantDesignManager || role.name === ROLENAME.DesignUser)) {
            obj.selectedRole = usersData?.role ? usersData?.role : ROLENAME.DesignUser
        } else if (userDetails.roles.find(role => role.name === ROLENAME.SalesManager || role.name === ROLENAME.AssistantSalesManager || role.name === ROLENAME.SalesUser)) {
            obj.selectedRole = usersData?.role ? usersData?.role : ROLENAME.SalesUser
        } else {
            obj.selectedRole = usersData?.role ? usersData?.role : (selectedRole ? selectedRole : "ALL")
        }
        props.setLoading(true);
        try {
            let response = await HttpService.overallSummaryDashBoardGroupWise(obj);


            setDynamicDataShow((prev) => prev + 1)

            if (!response.data || !response.data.organizedData || !response.data.organizedData.data) {
                console.error("Invalid response structure", response.data);
                props.setSnackInfo("Could not load grouped summary", "error");
                return;
            }

            const organizedData = response.data.organizedData.data;
            
            // Filter out totals row from organizedData - we'll render it manually
            const regularData = organizedData.filter(item => 
                item.year !== 'Grand Total' && 
                item.month !== 'Total' && 
                item._id !== 'Grand Total' &&
                !item.isTotalRow
            );
            
            // Get totals row if exists
            const totalsRowFromApi = organizedData.find(item => 
                item.year === 'Grand Total' || 
                item.month === 'Total' || 
                item._id === 'Grand Total' ||
                item.isTotalRow === true
            );
    
            const processedData = regularData.map(item => {
                const { totalCount, grandTotal, month, year, details, selectedRows, detailsArray } = item;
    
                // Extract headings that include '/'
                const uniqueHeadingsSet = new Set();
                if (detailsArray && detailsArray.length > 0) {
                    detailsArray.forEach(detail => {
                        Object.keys(detail).forEach(detailKey => {
                            if (detailKey.includes('/')) {
                                uniqueHeadingsSet.add(detailKey);
                            }
                        });
                    });
                }
    
                const filteredHeadings = Array.from(uniqueHeadingsSet);
                setStoreHeadings((prevStoreHeadings) => {
                    const newUniqueHeadings = new Set([...prevStoreHeadings, ...filteredHeadings]);
                    return Array.from(newUniqueHeadings);
                });
    
                if (selectedRows && selectedRows.length > 0) {
                    const concatenatedRows = selectedRows.join('/');
                    setStoreRowsData(prevStored => {
                        if (!prevStored.includes(concatenatedRows)) {
                            return [...prevStored, concatenatedRows];
                        }
                        return prevStored;
                    });
                }
    
                const filteredDetailsArray = detailsArray
                    ? detailsArray.filter(detail => {
                        const detailKey = Object.keys(detail)[0];
                        const countValue = detail[detailKey]?.count;
                        const valueAmount = detail[detailKey]?.value;
                        return countValue !== undefined && valueAmount !== undefined;
                    })
                    : [];
    
                return {
                    month,
                    year,
                    totalCount,
                    grandTotal,
                    details: details || [],
                    detailsArray: filteredDetailsArray,
                    selectedRows: selectedRows
                };
            });
            
            // Store totals row separately
            if (totalsRowFromApi) {
                setStoreTotalsRow(totalsRowFromApi);
            } else {
                setStoreTotalsRow(null);
            }
            
            setGroupedTable(processedData);
            setShowNewTable(true);
            
        } catch (error) {
            console.error("Error in overallSummaryDashBoardGroupWise:", error);
            props.setSnackInfo(error?.response?.data ?? "Something Went Wrong", "error")
        }
        props.setLoading(false);
    }


    const handleExpData = async (newvalue) => {
        props.setLoading(true);

        if (dynamicDataShow !== 0) {
            await overallSummaryDashBoardGroupWise(selectedLocation, newvalue, selectedUsers, selectedSearchType, searchInput, selectedGroup)
        } else {
            await overallsummary(selectedLocation, newvalue, selectedUsers, selectedSearchType, searchInput)
        }
        props.setLoading(false);
    }

    const handleDelete = async (groupId, mainId) => {
        try {
            await HttpService.dynamicDeleteGroup(groupId, mainId); // Call the delete API
            // Remove the group from the frontend state
            setStoreGroupDetails((prevDetails) => prevDetails.filter(item => item._id !== groupId));
            dynamicGroupDetails()
        } catch (error) {
            console.error('Error deleting group:', error);
        }
    };


    const defaultView = () => {
        setShowNewTable(false);
        setUserGroupDetails([])
        setSelectedExperienceCenter([])
        setPivotDatefilters({ startDate: "", endDate: "" })
        setSelectedValue("")
        setSelectedUsers([])
        setSelectedSearchType("")
        setGroupedTable([])
        setSearchInput("")
        setStoreHeadings([])
        setDynamicDataShow(0)
        setStoreRowsData([])
        setStoreTotalsRow(null)
    }
    useEffect(() => {
        dynamicGroupDetails()
    }, [showNewTable])

    const showNotesInfo = (data)=>{
        setShowLatestNotes(true);
        setLatestNotesMsg(data.latestNotesMsg);
        setLatestNotesDate(data.latestNotesDate)
    }

    const showLostInfo = (data)=>{
        setShowLostNotes(true);
        setLostDealActivity(data.lostDealActivity);
        setLostDealActivityDate(data.lostDate)
    }

    const setLatestDealData = (data) => {
        setLatestDealModal(true);
        setLatestDealMsg(data.latestDealMsg);
        setLatestDealDate(data.latestDealDate);
    }

    const showNpsData = (data)=>{
        setShowNpsFormModal(true);
        setNpsCreatedDate(data.npsCreatedDate);
        setNpsFeedback(data.npsFeedback);
        setNpsFormData(data.npsFormData);
        setIsManagerInvolved(data.isManagerInvolved);
        setManagerSatisfactionForm(data.managerSatisfactionForm);
        const avg = data?.npsFormData?.reduce((sum, item) => sum + Number(item.actionSelected), 0) / data.npsFormData.length;
        const score = avg.toFixed(2);
        setAvgScore(score)
    }

    return (
        <div className="App">
            <br />
            {isCallingDesk && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '0 16px 8px', color: '#1C375D', fontSize: 20, fontWeight: 600 }}>
                    Calling Desk
                </div>
            )}

            <div>


                <Paper className={classes.paper}>
                    <Grid
                        container
                        spacing={2}
                        justifyContent={
                            hasAnyRole(userDetails, FILTER_ALIGN_ROLES)
                                ? "flex-start"
                                : "center"
                        }
                        alignItems="center"
                        wrap="nowrap"  // Prevents wrapping to the next line, keeps everything in one row
                    >
                        <Grid item xs={12} sm={3}>
                            <FormControl style={{ width: '100%', marginTop: '0.4rem' }}>
                                <Autocomplete
                                    options={storeGroupDetails}
                                    value={storeUserGroupDetails}
                                    getOptionLabel={(option) => option?.name || ''}
                                    onChange={handleGroupChange}
                                    renderOption={(option, { selected }) => (
                                        <React.Fragment>
                                            <span style={{ flexGrow: 1 }}>{option?.name || ''}</span>
                                            {/* Add delete button */}
                                            <IconButton
                                                edge="end"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDelete(option);
                                                }}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </React.Fragment>
                                    )}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Select a Group" variant="outlined" />
                                    )}
                                />
                            </FormControl>
                        </Grid>
                        {hasAnyRole(userDetails, SCOPE_FILTER_ROLES)
                            && userDetails.locationId.length > 1 && (
                                <Grid item xs={12} sm={3}>
                                    <FormControl style={{ width: '100%' }}>
                                        <Autocomplete
                                            multiple
                                            value={selectedLocation}
                                            onChange={handleMultipleLocationData}
                                            options={commonLocation}
                                            getOptionLabel={(option) => option.name}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Select Location"
                                                    variant="outlined"
                                                />
                                            )}
                                        />
                                    </FormControl>
                                </Grid>
                            )}
                        {hasAnyRole(userDetails, SCOPE_FILTER_ROLES)
                            && userDetails.experienceCenterId.length > 1 && (
                                <Grid item xs={12} sm={3}>
                                    <FormControl style={{ width: '100%', marginTop: '0.4rem' }}>
                                        <Autocomplete
                                            multiple
                                            value={selectedExperienceCenter}
                                            onChange={handleMultipleExpData}
                                            options={commonExpCenter}
                                            getOptionLabel={(option) => option.name}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Select Exp-Center"
                                                    variant="outlined"
                                                />
                                            )}
                                        />
                                    </FormControl>
                                </Grid>
                            )}
                        <Grid item xs={12} sm={2}>
                            <TextField
                                id="startDate"
                                name="startDate"
                                label="From Date"
                                type="date"
                                variant="outlined"
                                value={storeDateFilter.startDate}
                                onChange={storePivotDate}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} sm={2}>
                            <TextField
                                id="endDate"
                                name="endDate"
                                label="End Date"
                                type="date"
                                variant="outlined"
                                value={storeDateFilter.endDate}
                                onChange={storePivotDate}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} sm={2}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handlePivotDateFilter}
                                style={{
                                    backgroundColor: "#F50057",
                                    color: "#fff",
                                    padding: "0.4rem 2rem 2rem 2rem",
                                }}
                                className={classes.submitButton}
                            >
                                Submit
                            </Button>
                        </Grid>
                    </Grid>

                    <Grid container spacing={2} justifyContent="center" alignItems="center">
                        <Grid item xs={12} sm={2}>
                            <FormControl style={{ width: '70%', marginTop: "0.4rem" }}>
                                <TextField
                                    select
                                    labelId="project-doc"
                                    label="5 % Payment"
                                    variant="outlined"
                                    value={selectedValue}
                                    onChange={(e) => handleSelectChange(e)}
                                >
                                    <MenuItem value="2">All</MenuItem>
                                    <MenuItem value="1">YES</MenuItem>
                                    <MenuItem value="0">NO</MenuItem>
                                </TextField>
                            </FormControl>
                        </Grid>

                        {hasAnyRole(userDetails, HIERARCHY_FILTER_ROLES) && (
                            <>
                                <Grid item xs={12} sm={3}>
                                    <FormControl style={{ width: '90%', marginTop: "0.4rem" }}>
                                        <TextField
                                            select
                                            labelId="project-doc"
                                            label="Select Role"
                                            variant="outlined"
                                            value={selectedRole}
                                            onChange={(e) => handleSelectRoles(e)}
                                        // disabled={pivot === "lost" && !selectedDepartment}  // Disable if selectedDepartment is not set and pivot is "lost"
                                        >
                                            {/* Create an array of MenuItem elements instead of using a fragment */}
                                            {[
                                                <MenuItem key="ALL" value="ALL">ALL</MenuItem>,
                                                ...(userDetails.departmentId === salesDepartment ? [
                                                    <MenuItem key={ROLENAME.DGM_Sales} value={ROLENAME.DGM_Sales}>DGM Sales</MenuItem>,
                                                    <MenuItem key={ROLENAME.SalesManager} value={ROLENAME.SalesManager}>Sales Manager</MenuItem>,
                                                    <MenuItem key={ROLENAME.AssistantSalesManager} value={ROLENAME.AssistantSalesManager}>Assistant Sales Manager</MenuItem>,
                                                    <MenuItem key={ROLENAME.SalesUser} value={ROLENAME.SalesUser}>Executive</MenuItem>
                                                ] : [
                                                    <MenuItem key={ROLENAME.DGM_Design} value={ROLENAME.DGM_Design}>DGM Design</MenuItem>,
                                                    <MenuItem key={ROLENAME.OperationDesignManager} value={ROLENAME.OperationDesignManager}>Operation Design Manager</MenuItem>,
                                                    <MenuItem key={ROLENAME.AssistantDesignManager} value={ROLENAME.AssistantDesignManager}>Assistant Design Manager</MenuItem>,
                                                    <MenuItem key={ROLENAME.DesignUser} value={ROLENAME.DesignUser}>Designer</MenuItem>
                                                ])
                                            ]}
                                        </TextField>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <FormControl style={{ width: '100%', marginTop: '0.30rem' }}>
                                        <Autocomplete
                                            multiple
                                            value={selectedUsers}
                                            onChange={(e, v) => handleMultipleUsers(e, v)}
                                            options={usersData}
                                            getOptionLabel={(option) => option.name}
                                            renderInput={(params) => <TextField {...params} label="Select Users" variant="outlined" />}
                                        />
                                    </FormControl>
                                </Grid>
                            </>
                        )}
                        {(userDetails?.roles && userDetails.roles.some(role => [ROLENAME.AGM_Design].includes(role.name))) && (
                             <>
                                 <Grid item xs={12} sm={2}>
                                     <FormControl style={{ width: '80%', marginTop: "0.4rem" }}>
                                         <TextField
                                             select
                                             labelId="project-doc"
                                             label="Select Role"
                                             variant="outlined"
                                             value={selectedRole}
                                             onChange={(e) => handleSelectRoles(e)}
                                         >
                                             <MenuItem value={ROLENAME.OperationDesignManager}>Operation Design Manager</MenuItem>
                                             <MenuItem value={ROLENAME.AssistantDesignManager}>Assistant Design Manager</MenuItem>
                                             <MenuItem value={ROLENAME.DesignUser}>Designer</MenuItem>
                                         </TextField>
                                     </FormControl>
                                 </Grid>
                                 <Grid item xs={12} sm={3}>
                                     <FormControl style={{ width: '100%', marginTop: '0.30rem' }}>
                                         <Autocomplete
                                             multiple
                                             value={selectedUsers}
                                             onChange={(e, v) => handleMultipleUsers(e, v)}
                                             options={usersData}
                                             getOptionLabel={(option) => option.name}
                                             renderInput={(params) => <TextField {...params} label="Select Users" variant="outlined" />}
                                         />
                                     </FormControl>
                                 </Grid>
                             </>
                        )}  
                        {(userDetails?.roles && userDetails.roles.some(role => [ROLENAME.SalesManager, ROLENAME.OperationDesignManager,ROLENAME.AGMSales].includes(role.name))) && (
                            <>
                                <Grid item xs={12} sm={3}>
                                    <FormControl style={{ width: '90%', marginTop: "0.4rem" }}>
                                        <TextField
                                            select
                                            labelId="project-doc"
                                            label="Select Role"
                                            variant="outlined"
                                            value={selectedRole}
                                            onChange={(e) => handleSelectRoles(e)}
                                        // disabled={pivot === "lost" && !selectedDepartment}  // Disable if selectedDepartment is not set and pivot is "lost"
                                        >
                                            {/* Create an array of MenuItem elements instead of using a fragment */}
                                            {[
                                                ...(userDetails.departmentId === salesDepartment ? [
                                                    <MenuItem key={ROLENAME.SalesManager} value={ROLENAME.SalesManager}>Sales Manager</MenuItem>,
                                                    <MenuItem key={ROLENAME.AssistantSalesManager} value={ROLENAME.AssistantSalesManager}>Assistant Sales Manager</MenuItem>,
                                                    <MenuItem key={ROLENAME.SalesUser} value={ROLENAME.SalesUser}>Executive</MenuItem>
                                                ] : [
                                                    <MenuItem key={ROLENAME.OperationDesignManager} value={ROLENAME.OperationDesignManager}>Operation Design Manager</MenuItem>,
                                                    <MenuItem key={ROLENAME.AssistantDesignManager} value={ROLENAME.AssistantDesignManager}>Assistant Design Manager</MenuItem>,
                                                    <MenuItem key={ROLENAME.DesignUser} value={ROLENAME.DesignUser}>Designer</MenuItem>
                                                ])
                                            ]}
                                        </TextField>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <FormControl style={{ width: '100%', marginTop: '0.30rem' }}>
                                        <Autocomplete
                                            multiple
                                            value={selectedUsers}
                                            onChange={(e, v) => handleMultipleUsers(e, v)}
                                            options={usersData}
                                            getOptionLabel={(option) => option.name}
                                            renderInput={(params) => <TextField {...params} label="Select Users" variant="outlined" />}
                                        />
                                    </FormControl>
                                </Grid>
                            </>
                        )}

                        {(userDetails?.roles && userDetails.roles.some(role => [ROLENAME.AssistantSalesManager, ROLENAME.AssistantDesignManager].includes(role.name))) && (
                            <>
                                <Grid item xs={12} sm={3}>
                                    <FormControl style={{ width: '90%', marginTop: "0.4rem" }}>
                                        <TextField
                                            select
                                            labelId="project-doc"
                                            label="Select Role"
                                            variant="outlined"
                                            value={selectedRole}
                                            onChange={(e) => handleSelectRoles(e)}
                                        // disabled={pivot === "lost" && !selectedDepartment}  // Disable if selectedDepartment is not set and pivot is "lost"
                                        >
                                            {/* Create an array of MenuItem elements instead of using a fragment */}
                                            {[
                                                ...(userDetails.departmentId === salesDepartment ? [
                                                    <MenuItem key={ROLENAME.AssistantSalesManager} value={ROLENAME.AssistantSalesManager}>Assistant Sales Manager</MenuItem>,
                                                    <MenuItem key={ROLENAME.SalesUser} value={ROLENAME.SalesUser}>Executive</MenuItem>
                                                ] : [
                                                    <MenuItem key={ROLENAME.AssistantDesignManager} value={ROLENAME.AssistantDesignManager}>Assistant Design Manager</MenuItem>,
                                                    <MenuItem key={ROLENAME.DesignUser} value={ROLENAME.DesignUser}>Designer</MenuItem>
                                                ])
                                            ]}
                                        </TextField>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <FormControl style={{ width: '100%', marginTop: '0.30rem' }}>
                                        <Autocomplete
                                            multiple
                                            size="medium"
                                            value={selectedUsers}
                                            onChange={(e, v) => handleMultipleUsers(e, v)}
                                            ListboxProps={{ style: { maxHeight: "15rem" }, position: "bottom-start" }}
                                            options={usersData}
                                            getOptionLabel={(option) => option.name}
                                            renderInput={(params) => <TextField {...params} label="Select Users" variant="outlined" />}
                                        />
                                    </FormControl>
                                </Grid>
                            </>
                        )}
                        <Grid item xs={12} sm={3}>
                            <FormControl style={{ marginTop: '0.30rem' }}>
                                <Button variant="contained" color="primary" onClick={defaultView} style={{ backgroundColor: "#F47E46", color: "#FFF2EB", padding: "0.4rem 2rem 2rem 2rem" }}>
                                    Default Table</Button>
                            </FormControl>
                        </Grid>
                    </Grid>
                    <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', marginTop: '30px' }}>
                        {/* Search Type Dropdown */}
                        <TextField
                            select
                            labelId="demo-simple-select-label"
                            id="demo-simple-select"
                            value={selectedSearchType}
                            label="Search Type"
                            name="searchType"
                            onChange={(e) => handleSearchType(e.target.value)}
                            variant="outlined"
                            style={{ width: '30%', marginRight: '2%' }}
                            size="small"
                        >
                            {searchTypes && searchTypes?.map((el, index) => (
                                <MenuItem key={index} value={el}>{el}</MenuItem>
                            ))}
                        </TextField>

                        {/* Search Input */}
                        <TextField
                            label="Enter the input"
                            name="searched"
                            size="small"
                            value={searchInput}
                            onChange={(e) => { setSearchInput(e.target.value) }}
                            onKeyDown={handleKeyDown}  // Listening for the "Enter" key press
                            variant="outlined"
                            style={{ width: '80%' }}
                        />

                        {/* Search Button */}
                        <Button
                            disabled={!selectedSearchType || !searchInput}
                            variant="contained"
                            color="primary"
                            size="medium"
                            style={{ alignSelf: "center", marginLeft: 20 }}
                            className={classes.leadSearchButton}
                            onClick={() => handleSearch(searchInput.toUpperCase())}
                        >
                            Search
                        </Button>

                        {/* Clear Filter Button */}
                        <Button
                            variant="contained"
                            color="secondary"
                            size="medium"
                            style={{ marginLeft: 10 }}
                            className={classes.leadClearButton}
                            onClick={clearDateFilter}
                        >
                            Clear Filter
                        </Button>
                    </div>
                </Paper>
            </div>
            <br />


            <div>

                <>
                    <Box
                        display="flex"
                        alignItems="center"
                        mt={2}
                        backgroundColor="#3F51B5"
                        p={2} // padding inside the Box component

                    >
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleToggleGroupingMode}
                            style={{
                                marginRight: '8px',
                                padding: "0.4rem 2rem 2rem 2rem",
                                backgroundColor: "#F47E46",
                                color: "#FFF2EB",
                            }}
                        >
                            {isGroupingMode ? 'Cancel Grouping' : 'Create Group'}
                        </Button>
                        <Grid item>
                            <Button
                                onClick={handleRefreshClick}
                                variant="contained"
                                disabled={isDisabled}
                                style={{ 
                                backgroundColor: isDisabled ? "#cccccc" : "#F47E46", 
                                padding: "0.4rem 2rem 2rem 2rem",
                                color: "#FFF2EB",
                               
                                }}
                            >
                                {isDisabled 
                                ? `Wait (${formatTimeLeft()})` 
                                : "Fetch New Data"}
                            </Button>
                        </Grid>

                        <Grid item>
                            <Button
                                variant="contained"
                                color="primary"
                                style={{
                                    backgroundColor: "#F47E46",
                                    color: "#FFF2EB",
                                    padding: "0.4rem 2rem 2rem 2rem",
                                    marginLeft: 8
                                }}
                                onClick={() => setIsTransposed(!isTransposed)}
                            >
                                {isTransposed ? 'Show Normal View' : 'Transpose Table'}
                            </Button>
                        </Grid>

                        <Grid item>
                            <CSVLink
                                data={normalTableCsvData}
                                filename={`Design_Summary_Normal_${moment().format('YYYY-MM-DD')}.csv`}
                                ref={normalTableCsvButtonRef}
                                style={{ textDecoration: 'none', display: 'none' }}
                            />
                            <CSVLink
                                data={transposeTableCsvData}
                                filename={`Design_Summary_Transpose_${moment().format('YYYY-MM-DD')}.csv`}
                                ref={transposeTableCsvButtonRef}
                                style={{ textDecoration: 'none', display: 'none' }}
                            />
                            <Button
                                type="primary"
                                icon={<DownloadOutlined />}
                                style={{
                                    backgroundColor: "#355025",
                                    color: "#FFF2EB",
                                    padding: "0.4rem 2rem",
                                    marginLeft: 8,
                                    border: "none"
                                }}
                                onClick={() => {
                                    // Always regenerate both to ensure latest data
                                    generateNormalTableCsv();
                                    generateTransposeTableCsv();
                                    
                                    setTimeout(() => {
                                        // Export based on current view
                                        if (isTransposed) {
                                            if (transposeTableCsvButtonRef.current && transposeTableCsvData.length > 0) {
                                                transposeTableCsvButtonRef.current.link.click();
                                            }
                                        } else {
                                            if (normalTableCsvButtonRef.current && normalTableCsvData.length > 0) {
                                                normalTableCsvButtonRef.current.link.click();
                                            }
                                        }
                                    }, 300);
                                }}
                            >
                                Download CSV
                            </Button>
                        </Grid>       

                        {isGroupingMode && (
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleClickOpen}
                                style={{
                                    color: 'white', // white font color
                                    backgroundColor: '#3F51B5',
                                    padding: "0.4rem 2rem 2rem 2rem"
                                }}
                                className={classes.submitButton}
                            >
                                Submit Table
                            </Button>
                        )}
                    </Box>

                    {/* Dialog Box */}
                    <Dialog open={open} onClose={handleClose}>
                        <DialogTitle>Enter Group Name</DialogTitle>
                        <DialogContent>
                            <TextField
                                autoFocus
                                margin="dense"
                                label="Enter Group Name"
                                type="text"
                                fullWidth
                                value={groupName}
                                onChange={(e) => setGroupName(e.target.value)}
                            />
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleClose} color="secondary">
                                Cancel
                            </Button>
                            <Button onClick={handleSubmit} color="primary">
                                Submit
                            </Button>
                        </DialogActions>
                    </Dialog>
                </>


                {!showNewTable ? (
                    <>
                        {isTransposed && (
                            <div style={{ maxHeight: '600px', overflow: 'auto' }}>
                                <TableContainer component={Paper} classes={{ root: classes.customTableContainer }} style={{ marginTop: '30px' }}>
                                    <Table style={{ minWidth: '600px' }}>
                                        <TableHead style={{ position: 'sticky', top: 0, zIndex: 9, background: 'white' }}>
                                            <StyledTableRow>
                                                <StyledTableCell className={classes.bordercolors} style={{ position: 'sticky', left: 0, zIndex: 9, textAlign: "center", fontWeight: 'bold' }}>Metric</StyledTableCell>
                                                {store?.slice(0, -1).map((ele, idx) => (
                                                    <StyledTableCell key={idx} className={classes.bordercolors} style={{ textAlign: 'center', fontWeight: 'bold' }}>{months[ele?.month]}-{ele?._id?.year % 100}</StyledTableCell>
                                                ))}
                                                <StyledTableCell className={classes.bordercolors} style={{ textAlign: 'center', fontWeight: 'bold' }}>Grand Total</StyledTableCell>
                                            </StyledTableRow>
                                        </TableHead>
                                        <TableBody>
                                            {[
                                                { dataKey: 'designerNotAssignedPendingWithSalesCount', metricKey: 'designerNotAssignedPendingWithSales', label: 'Designer Not Assigned Pending With Sales - Count' },
                                                { dataKey: 'designerNotAssignedPendingWithSales', metricKey: 'designerNotAssignedPendingWithSales', label: 'Designer Not Assigned Pending With Sales - Value' },
                                                { dataKey: 'designerNotAssignedKOMRejectedCount', metricKey: 'designerNotAssignedKOMRejected', label: 'Designer Not Assigned KOM Rejected - Count' },
                                                { dataKey: 'designerNotAssignedKOMRejected', metricKey: 'designerNotAssignedKOMRejected', label: 'Designer Not Assigned KOM Rejected - Value' },
                                                { dataKey: 'designerNotAssignedPendingWithDesignCount', metricKey: 'designerNotAssignedPendingWithDesign', label: 'Designer Not Assigned - Pending with Design - Count' },
                                                { dataKey: 'designerNotAssignedPendingWithDesign', metricKey: 'designerNotAssignedPendingWithDesign', label: 'Designer Not Assigned - Pending with Design - Value' },
                                                { dataKey: 'designKickOffInternalCount', metricKey: 'designKickOffInternal', label: 'Design KickOff Internal - Count' },
                                                { dataKey: 'designKickOffInternal', metricKey: 'designKickOffInternal', label: 'Design KickOff Internal - Value' },
                                                { dataKey: 'designKickOffCustomerCount', metricKey: 'designKickOffCustomer', label: 'Design KickOff Customer - Count' },
                                                { dataKey: 'designKickOffCustomer', metricKey: 'designKickOffCustomer', label: 'Design KickOff Customer - Value' },
                                                { dataKey: 'designCount', metricKey: 'design', label: 'Design - Count' },
                                                { dataKey: 'design', metricKey: 'design', label: 'Design - Value' },
                                                { dataKey: 'designHoldcount', metricKey: 'design-Hold', label: 'Design-Hold - Count' },
                                                { dataKey: 'designHoldGrand', metricKey: 'design-Hold', label: 'Design-Hold - Value' },
                                                { dataKey: 'completedCount', metricKey: 'completed', label: 'Design Signed Off - Count' },
                                                { dataKey: 'completed', metricKey: 'completed', label: 'Design Signed Off - Value' },
                                                { dataKey: 'lostcount', metricKey: 'lost', label: 'Lost - Count' },
                                                { dataKey: 'lost', metricKey: 'lost', label: 'Lost - Value' },
                                                { dataKey: 'totalCount', metricKey: 'total', label: 'Grand Total - Count' },
                                                { dataKey: 'grandTotal', metricKey: 'total', label: 'Grand Total - Value' }
                                            ].map((row) => (isCallingDesk && row.dataKey !== getCallingDeskCountKey(row.metricKey)) ? (
                                                <StyledTableRow key={row.dataKey}>
                                                <StyledTableCell style={{ position: 'sticky', left: 0, zIndex: 1, textAlign: 'center', border: '1px solid #eee5e5', background: '#1C375D', color: 'white', fontWeight: 'bold' }}>{row.label.replace(' - Value', ` - ${valueColumnLabel}`)}</StyledTableCell>
                                                {store?.slice(0, -1).map((ele, idx) => renderCallingDeskCell(idx, ele, row.metricKey, ele?.[getCallingDeskCountKey(row.metricKey)]))}
                                                {renderCallingDeskCell('grand-total', store[store.length - 1], row.metricKey, store[store.length - 1]?.[getCallingDeskCountKey(row.metricKey)])}
                                            </StyledTableRow>
                                            ) : (
                                                <StyledTableRow key={row.dataKey}>
                                                <StyledTableCell style={{ position: 'sticky', left: 0, zIndex: 1, textAlign: 'center', border: '1px solid #eee5e5', background: '#1C375D', color: 'white', fontWeight: 'bold' }}>{row.label}</StyledTableCell>
                                                {store?.slice(0, -1).map((ele, idx) => (
                                                    <StyledTableCell key={idx} className={classes.bordercolors} style={{ textAlign: 'center', cursor: 'pointer', background: 'white', textDecoration: 'underline' }} onClick={(e) => dataBasedOnClick(e, ele, row.metricKey)}>
                                                        {ele && ele[row.dataKey] !== undefined ? 
                                                            (row.dataKey.includes('Count') || row.dataKey === 'lostcount' || row.dataKey === 'designHoldcount' ? 
                                                                (ele[row.dataKey]) : 
                                                                formatNumberToTwoDecimalPlaces(ele[row.dataKey] || 0)) : 
                                                            0}
                                                    </StyledTableCell>
                                                ))}
                                                <StyledTableCell className={classes.bordercolors} style={{ textAlign: 'center', cursor: 'pointer', background: 'white', color: '#004d40', textDecoration: 'underline', fontWeight: 'bolder' }} onClick={(e) => dataBasedOnClick(e, store[store.length - 1], row.metricKey)}>
                                                    {store[store.length - 1] && store[store.length - 1][row.dataKey] !== undefined ? 
                                                        (row.dataKey.includes('Count') || row.dataKey === 'lostcount' || row.dataKey === 'designHoldcount' ? 
                                                            (store[store.length - 1][row.dataKey]) : 
                                                            formatNumberToTwoDecimalPlaces(store[store.length - 1][row.dataKey] || 0)) : 
                                                        0}
                                                </StyledTableCell>
                                            </StyledTableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </div>
                        )}
                        {!isTransposed && (
                        <div style={{ maxHeight: '600px', overflow: 'auto' }}>
                            <TableContainer component={Paper} classes={{ root: classes.customTableContainer }} style={{ marginTop: '30px' }}>
                                <Table style={{ minWidth: '600px' }}>
                                    <TableHead style={{ position: 'sticky', top: 0, zIndex: 9, background: 'white' }}>
                                        <StyledTableRow>
                                            {
                                                <StyledTableCell className={classes.bordercolors} style={{ position: 'sticky', left: 0, zIndex: 9, textAlign: "center" }} rowSpan={2}>Month And Year</StyledTableCell>
                                            }
                                            <StyledTableCell colSpan={2} className={classes.bordercolors} style={{ textAlign: 'center' }}>Designer Not Assigned Pending With Sales {isGroupingMode && <Checkbox onChange={(e) => handleHeadingSelect('designerNotAssignedPendingWithSales', e.target.checked)} />}</StyledTableCell>
                                            <StyledTableCell colSpan={2} className={classes.bordercolors} style={{ textAlign: 'center' }}>Designer Not Assigned KOM Rejected {isGroupingMode && <Checkbox onChange={(e) => handleHeadingSelect('designerNotAssignedKOMRejected', e.target.checked)} />}</StyledTableCell>
                                            <StyledTableCell colSpan={2} className={classes.bordercolors} style={{ textAlign: 'center' }}>Designer Not Assigned - Pending with Design {isGroupingMode && <Checkbox onChange={(e) => handleHeadingSelect('designerNotAssignedPendingWithDesign', e.target.checked)} />}</StyledTableCell>
                                            <StyledTableCell colSpan={2} className={classes.bordercolors} style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                                                {renderDesignSummaryColumnHeader('Design KickOff Internal', {
                                                    eyeClick: openDesignKickOffInternalDaysSummary,
                                                    eyeTitle: 'View Design Kick-Off Internal pending days by experience centre',
                                                    headingKey: 'designKickOffInternal',
                                                })}
                                            </StyledTableCell>
                                            <StyledTableCell colSpan={2} className={classes.bordercolors} style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                                                {renderDesignSummaryColumnHeader('Design KickOff Customer', {
                                                    eyeClick: openDesignKickOffCustomerDaysSummary,
                                                    eyeTitle: 'View Design Kick-Off Customer pending days by experience centre',
                                                    headingKey: 'designKickOffCustomer',
                                                })}
                                            </StyledTableCell>
                                            <StyledTableCell colSpan={2} className={classes.bordercolors} style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                                                {renderDesignSummaryColumnHeader('Design', {
                                                    eyeClick: openDesignStageDaysSummary,
                                                    eyeTitle: 'View Design stage days break-up',
                                                    headingKey: 'design',
                                                })}
                                            </StyledTableCell>
                                            <StyledTableCell colSpan={2} className={classes.bordercolors} style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                                                {renderDesignSummaryColumnHeader('Design-Hold', {
                                                    eyeClick: openDesignHoldAnalysis,
                                                    eyeTitle: 'Open Design Hold Analytics',
                                                    headingKey: 'designHoldGrand',
                                                })}
                                            </StyledTableCell>
                                            <StyledTableCell colSpan={2} className={classes.bordercolors} style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                                                {renderDesignSummaryColumnHeader('Design Signed Off', {
                                                    eyeClick: openInternalDsoSummary,
                                                    eyeTitle: 'Open Internal DSO Summary',
                                                    headingKey: 'completed',
                                                })}
                                            </StyledTableCell>
                                            <StyledTableCell colSpan={2} className={classes.bordercolors} style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                                                {renderDesignSummaryColumnHeader('Lost', {
                                                    eyeClick: openLostDataAnalysis,
                                                    eyeTitle: 'Open Lost Data Analysis',
                                                    headingKey: 'lost',
                                                })}
                                            </StyledTableCell>
                                            <StyledTableCell colSpan={2} className={classes.bordercolors} style={{
                                                fontSize: '15px',
                                                textAlign: 'center'
                                            }}>Grand Total</StyledTableCell>
                                        </StyledTableRow>
                                        <StyledTableRow>
                                            <StyledTableCell className={classes.bordercolors}>Count</StyledTableCell>
                                            <StyledTableCell className={classes.bordercolors}>{valueColumnLabel}</StyledTableCell>
                                            <StyledTableCell className={classes.bordercolors}>Count</StyledTableCell>
                                            <StyledTableCell className={classes.bordercolors}>{valueColumnLabel}</StyledTableCell>
                                            <StyledTableCell className={classes.bordercolors}>Count</StyledTableCell>
                                            <StyledTableCell className={classes.bordercolors}>{valueColumnLabel}</StyledTableCell>
                                            <StyledTableCell className={classes.bordercolors}>Count</StyledTableCell>
                                            <StyledTableCell className={classes.bordercolors}>{valueColumnLabel}</StyledTableCell>
                                            <StyledTableCell className={classes.bordercolors}>Count</StyledTableCell>
                                            <StyledTableCell className={classes.bordercolors}>{valueColumnLabel}</StyledTableCell>
                                            <StyledTableCell className={classes.bordercolors}>Count</StyledTableCell>
                                            <StyledTableCell className={classes.bordercolors}>{valueColumnLabel}</StyledTableCell>
                                            <StyledTableCell className={classes.bordercolors}>Count</StyledTableCell>
                                            <StyledTableCell className={classes.bordercolors}>{valueColumnLabel}</StyledTableCell>
                                            <StyledTableCell className={classes.bordercolors}>Count</StyledTableCell>
                                            <StyledTableCell className={classes.bordercolors}>{valueColumnLabel}</StyledTableCell>
                                            <StyledTableCell className={classes.bordercolors}>Count</StyledTableCell>
                                            <StyledTableCell className={classes.bordercolors}>{valueColumnLabel}</StyledTableCell>
                                            <StyledTableCell className={classes.bordercolors}>Count</StyledTableCell>
                                            <StyledTableCell className={classes.bordercolors}>{valueColumnLabel}</StyledTableCell>
                                        </StyledTableRow>
                                    </TableHead>
                                    <TableHead>
                                        {store.map((ele, index) => (
                                            <StyledTableRow key={index}>
                                                {
                                                    <>
                                                        {index === store.length - 1 ?
                                                            <StyledTableCell className={classes.bordercolors} style={{
                                                                fontSize: index === store.length - 1 ? '15px' : '13px',
                                                                fontWeight: index === store.length - 1 ? 'bolder' : 'normal',
                                                                left: 0,
                                                                position: 'sticky',
                                                                // Above the Calling Desk call buttons scrolling under it.
                                                                zIndex: 1,
                                                                textAlign: 'center'
                                                            }}>{ele?._id?.year}</StyledTableCell> :
                                                            <StyledTableCell className={classes.bordercolors} style={{
                                                                fontSize: index === store.length - 1 ? '15px' : '13px',
                                                                fontWeight: index === store.length - 1 ? 'bolder' : 'normal',
                                                                left: 0,
                                                                position: 'sticky',
                                                                zIndex: 1,
                                                                textAlign: "center"
                                                            }}>
                                                                {months[ele?.month]}-{ele?._id?.year % 100}
                                                                {isGroupingMode &&
                                                                    <Checkbox
                                                                        onChange={(e) =>
                                                                            handleRowSelect(
                                                                                `${ele?._id?.month}`, // month as a string
                                                                                ele?._id?.year,      // full year as number
                                                                                e.target.checked      // checked status
                                                                            )
                                                                        }
                                                                    />
                                                                }

                                                                {/* {ele?.month}/{ele?._id?.year % 100} */}
                                                            </StyledTableCell>
                                                        }
                                                    </>
                                                }

                                                {isCallingDesk ? CALLING_DESK_STAGE_COLUMNS.map(({ status, countKey }) => {
                                                    const isLastRow = index === store.length - 1;
                                                    const countStyle = status === "total"
                                                        ? { color: "#004d40", fontWeight: 'bolder' }
                                                        : { color: !isLastRow ? "#1C375D" : "#355025", fontWeight: !isLastRow ? 'normal' : 'bolder' };
                                                    return (
                                                        <React.Fragment key={status}>
                                                            <StyledTableCell className={classes.bordercolors} style={{ textAlign: "center", cursor: "pointer", textDecoration: "underline", background: "white", ...countStyle }} onClick={(e) => dataBasedOnClick(e, ele, status)}>{ele?.[countKey]}</StyledTableCell>
                                                            {renderCallingDeskCell(`${status}-call`, ele, status, ele?.[countKey])}
                                                        </React.Fragment>
                                                    );
                                                }) : (
                                                <>
                                                <StyledTableCell className={classes.bordercolors} style={{ textAlign: "center", cursor: "pointer", cursor: "pointer", color: index !== store.length - 1 ? "#1C375D" : "#355025", textDecoration: "underline", fontWeight: index !== store.length - 1 ? 'normal' : 'bolder', background: "white" }} onClick={(e) => dataBasedOnClick(e, ele, "designerNotAssignedPendingWithSales")}>{ele.designerNotAssignedPendingWithSalesCount}</StyledTableCell>
                                                <StyledTableCell className={classes.bordercolors} style={{ textAlign: "center", cursor: "pointer", cursor: "pointer", color: index !== store.length - 1 ? "#1C375D" : "#355025", textDecoration: "underline", fontWeight: index !== store.length - 1 ? 'normal' : 'bolder', background: "white" }} onClick={(e) => dataBasedOnClick(e, ele, "designerNotAssignedPendingWithSales")}>{(ele.designerNotAssignedPendingWithSales)?.toFixed(2)}</StyledTableCell>
                                                <StyledTableCell className={classes.bordercolors} style={{ textAlign: "center", cursor: "pointer", cursor: "pointer", color: index !== store.length - 1 ? "#1C375D" : "#355025", textDecoration: "underline", fontWeight: index !== store.length - 1 ? 'normal' : 'bolder', background: "white" }} onClick={(e) => dataBasedOnClick(e, ele, "designerNotAssignedKOMRejected")}>{ele.designerNotAssignedKOMRejectedCount}</StyledTableCell>
                                                <StyledTableCell className={classes.bordercolors} style={{ textAlign: "center", cursor: "pointer", cursor: "pointer", color: index !== store.length - 1 ? "#1C375D" : "#355025", textDecoration: "underline", fontWeight: index !== store.length - 1 ? 'normal' : 'bolder', background: "white" }} onClick={(e) => dataBasedOnClick(e, ele, "designerNotAssignedKOMRejected")}>{(ele.designerNotAssignedKOMRejected)?.toFixed(2)}</StyledTableCell>
                                                <StyledTableCell className={classes.bordercolors} style={{ textAlign: "center", cursor: "pointer", cursor: "pointer", color: index !== store.length - 1 ? "#1C375D" : "#355025", textDecoration: "underline", fontWeight: index !== store.length - 1 ? 'normal' : 'bolder', background: "white" }} onClick={(e) => dataBasedOnClick(e, ele, "designerNotAssignedPendingWithDesign")}>{(ele.designerNotAssignedPendingWithDesignCount)}</StyledTableCell>
                                                <StyledTableCell className={classes.bordercolors} style={{ textAlign: "center", cursor: "pointer", cursor: "pointer", color: index !== store.length - 1 ? "#1C375D" : "#355025", textDecoration: "underline", fontWeight: index !== store.length - 1 ? 'normal' : 'bolder', background: "white" }} onClick={(e) => dataBasedOnClick(e, ele, "designerNotAssignedPendingWithDesign")}>{(ele.designerNotAssignedPendingWithDesign)?.toFixed(2)}</StyledTableCell>
                                                <StyledTableCell className={classes.bordercolors} style={{ textAlign: "center", cursor: "pointer", cursor: "pointer", color: index !== store.length - 1 ? "#1C375D" : "#355025", textDecoration: "underline", fontWeight: index !== store.length - 1 ? 'normal' : 'bolder', background: "white" }} onClick={(e) => dataBasedOnClick(e, ele, "designKickOffInternal")}>{(ele.designKickOffInternalCount)}</StyledTableCell>
                                                <StyledTableCell className={classes.bordercolors} style={{ textAlign: "center", cursor: "pointer", cursor: "pointer", color: index !== store.length - 1 ? "#1C375D" : "#355025", textDecoration: "underline", fontWeight: index !== store.length - 1 ? 'normal' : 'bolder', background: "white" }} onClick={(e) => dataBasedOnClick(e, ele, "designKickOffInternal")}>{(ele.designKickOffInternal)?.toFixed(2)}</StyledTableCell>
                                                <StyledTableCell className={classes.bordercolors} style={{ textAlign: "center", cursor: "pointer", cursor: "pointer", color: index !== store.length - 1 ? "#1C375D" : "#355025", textDecoration: "underline", fontWeight: index !== store.length - 1 ? 'normal' : 'bolder', background: "white" }} onClick={(e) => dataBasedOnClick(e, ele, "designKickOffCustomer")}>{(ele.designKickOffCustomerCount)}</StyledTableCell>
                                                <StyledTableCell className={classes.bordercolors} style={{ textAlign: "center", cursor: "pointer", cursor: "pointer", color: index !== store.length - 1 ? "#1C375D" : "#355025", textDecoration: "underline", fontWeight: index !== store.length - 1 ? 'normal' : 'bolder', background: "white" }} onClick={(e) => dataBasedOnClick(e, ele, "designKickOffCustomer")}>{(ele.designKickOffCustomer)?.toFixed(2)}</StyledTableCell>
                                                <StyledTableCell className={classes.bordercolors} style={{ textAlign: "center", cursor: "pointer", cursor: "pointer", color: index !== store.length - 1 ? "#1C375D" : "#355025", textDecoration: "underline", fontWeight: index !== store.length - 1 ? 'normal' : 'bolder', background: "white" }} onClick={(e) => dataBasedOnClick(e, ele, "design")}>{ele.designCount}</StyledTableCell>
                                                <StyledTableCell className={classes.bordercolors} style={{ textAlign: "center", cursor: "pointer", cursor: "pointer", color: index !== store.length - 1 ? "#1C375D" : "#355025", textDecoration: "underline", fontWeight: index !== store.length - 1 ? 'normal' : 'bolder', background: "white" }} onClick={(e) => dataBasedOnClick(e, ele, "design")}>{(ele.design)?.toFixed(2)}</StyledTableCell>
                                                <StyledTableCell className={classes.bordercolors} style={{ textAlign: "center", cursor: "pointer", cursor: "pointer", color: index !== store.length - 1 ? "#1C375D" : "#355025", textDecoration: "underline", fontWeight: index !== store.length - 1 ? 'normal' : 'bolder', background: "white" }} onClick={(e) => dataBasedOnClick(e, ele, "design-Hold")}>{ele.designHoldcount}</StyledTableCell>
                                                <StyledTableCell className={classes.bordercolors} style={{ textAlign: "center", cursor: "pointer", cursor: "pointer", color: index !== store.length - 1 ? "#1C375D" : "#355025", textDecoration: "underline", fontWeight: index !== store.length - 1 ? 'normal' : 'bolder', background: "white" }} onClick={(e) => dataBasedOnClick(e, ele, "design-Hold")}>{(ele.designHoldGrand)?.toFixed(2)}</StyledTableCell>
                                                <StyledTableCell className={classes.bordercolors} style={{ textAlign: "center", cursor: "pointer", cursor: "pointer", color: index !== store.length - 1 ? "#1C375D" : "#355025", textDecoration: "underline", fontWeight: index !== store.length - 1 ? 'normal' : 'bolder', background: "white" }} onClick={(e) => dataBasedOnClick(e, ele, "completed")}>{ele.completedCount}</StyledTableCell>
                                                <StyledTableCell className={classes.bordercolors} style={{ textAlign: "center", cursor: "pointer", cursor: "pointer", color: index !== store.length - 1 ? "#1C375D" : "#355025", textDecoration: "underline", fontWeight: index !== store.length - 1 ? 'normal' : 'bolder', background: "white" }} onClick={(e) => dataBasedOnClick(e, ele, "completed")}>{(ele.completed)?.toFixed(2)}</StyledTableCell>
                                                <StyledTableCell className={classes.bordercolors} style={{ textAlign: "center", cursor: "pointer", cursor: "pointer", color: index !== store.length - 1 ? "#1C375D" : "#355025", textDecoration: "underline", fontWeight: index !== store.length - 1 ? 'normal' : 'bolder', background: "white" }} onClick={(e) => dataBasedOnClick(e, ele, "lost")}>{ele.lostcount}</StyledTableCell>
                                                <StyledTableCell className={classes.bordercolors} style={{ textAlign: "center", cursor: "pointer", cursor: "pointer", color: index !== store.length - 1 ? "#1C375D" : "#355025", textDecoration: "underline", fontWeight: index !== store.length - 1 ? 'normal' : 'bolder', background: "white" }} onClick={(e) => dataBasedOnClick(e, ele, "lost")}>{(ele.lost)?.toFixed(2)}</StyledTableCell>
                                                <StyledTableCell className={classes.bordercolors} style={{ textAlign: "center", cursor: "pointer", cursor: "pointer", color: "#004d40", textDecoration: "underline", fontWeight: 'bolder', background: "white", textAlign: "center" }} onClick={(e) => dataBasedOnClick(e, ele, "total")}>{ele?.totalCount}</StyledTableCell>
                                                <StyledTableCell className={classes.bordercolors} style={{ textAlign: "center", cursor: "pointer", cursor: "pointer", color: "#004d40", textDecoration: "underline", fontWeight: 'bolder', background: "white", textAlign: "center" }} onClick={(e) => dataBasedOnClick(e, ele, "total")}>{(ele?.grandTotal)?.toFixed(2)}</StyledTableCell>
                                                </>
                                                )}

                                            </StyledTableRow>
                                        ))}
                                    </TableHead>
                                </Table>
                            </TableContainer>
                        </div>
                        )}

                    </>)
                    :
                    <div style={{ maxHeight: '600px', overflow: 'auto' }}>
                        <TableContainer component={Paper} className={classes.customTableContainer} style={{ marginTop: '30px' }}>
                            <Table style={{ minWidth: '600px' }}>
                                <TableHead style={{ position: 'sticky', top: 0, zIndex: 9, background: 'white' }}>
                                    <TableRow>
                                        <StyledTableCell
                                            className={classes.bordercolors}
                                            rowSpan={2}
                                            style={designSummaryGroupedHeaderStyle({
                                                position: 'sticky',
                                                left: 0,
                                                top: 0,
                                                zIndex: 12,
                                                boxShadow: '2px 0 3px -1px rgba(0, 0, 0, 0.15)',
                                            })}>
                                            Month And Year
                                        </StyledTableCell>

                                        {/* Dynamically create headers for details */}
                                        {groupedTable[0]?.detailsArray?.map((detail, index) => {
                                            const detailKey = Object.keys(detail)[0];
                                            const isChecked = selectedHeadings[detailKey] || false;
                                            const mergedStages = getMergedStageLabels(detailKey);
                                            const columnTitle = formatGroupedColumnTitle(
                                                detailKey,
                                                storeUserGroupDetails?.name,
                                                index
                                            );

                                            return (
                                                <TableCell
                                                    key={index}
                                                    colSpan={2}
                                                    className={classes.bordercolors}
                                                    style={designSummaryGroupedHeaderStyle({ maxWidth: 160 })}
                                                >
                                                    <span className={classes.columnHeaderInner}>
                                                        <span style={{ whiteSpace: 'normal', lineHeight: 1.25 }}>{columnTitle}</span>
                                                        {mergedStages && (
                                                            <Tooltip
                                                                title={mergedStages.map((label) => `• ${label}`).join('\n')}
                                                                placement="top"
                                                            >
                                                                <IconButton
                                                                    type="button"
                                                                    size="small"
                                                                    className={classes.columnHeaderEye}
                                                                    style={{ color: '#fff' }}
                                                                    aria-label="View merged stages"
                                                                >
                                                                    <InfoIcon style={{ fontSize: 16 }} />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                        {detailKey === 'designKickOffInternal' && renderDesignSummaryEyeButton(
                                                            openDesignKickOffInternalDaysSummary,
                                                            'View Design Kick-Off Internal pending days by experience centre',
                                                            '#fff'
                                                        )}
                                                        {detailKey === 'designKickOffCustomer' && renderDesignSummaryEyeButton(
                                                            openDesignKickOffCustomerDaysSummary,
                                                            'View Design Kick-Off Customer pending days by experience centre',
                                                            '#fff'
                                                        )}
                                                        {detailKey === 'design' && renderDesignSummaryEyeButton(
                                                            openDesignStageDaysSummary,
                                                            'View Design stage days break-up',
                                                            '#fff'
                                                        )}
                                                        {detailKey === 'designHoldGrand' && renderDesignSummaryEyeButton(
                                                            openDesignHoldAnalysis,
                                                            'Open Design Hold Analytics',
                                                            '#fff'
                                                        )}
                                                        {detailKey === 'completed' && renderDesignSummaryEyeButton(
                                                            openInternalDsoSummary,
                                                            'Open Internal DSO Summary',
                                                            '#fff'
                                                        )}
                                                        {detailKey === 'lost' && renderDesignSummaryEyeButton(
                                                            openLostDataAnalysis,
                                                            'Open Lost Data Analysis',
                                                            '#fff'
                                                        )}
                                                        {isGroupingMode && (
                                                            <Checkbox
                                                                size="small"
                                                                style={{ padding: 4, color: 'white' }}
                                                                checked={isChecked}
                                                                onChange={(e) => handleHeadingSelectForSubGroups(detailKey, e.target.checked)}
                                                            />
                                                        )}
                                                    </span>
                                                </TableCell>
                                            );
                                        })}


                                        <TableCell colSpan={2} className={classes.bordercolors} style={designSummaryGroupedHeaderStyle()}>Grand Total</TableCell>
                                    </TableRow>

                                    <TableRow>
                                        {groupedTable[0]?.detailsArray?.map((_, index) => (
                                            <React.Fragment key={index}>
                                                <TableCell className={classes.bordercolors} style={designSummaryGroupedHeaderStyle()}>Count</TableCell>
                                                <TableCell className={classes.bordercolors} style={designSummaryGroupedHeaderStyle()}>{valueColumnLabel}</TableCell>
                                            </React.Fragment>
                                        ))}
                                        <TableCell className={classes.bordercolors} style={designSummaryGroupedHeaderStyle()}>Count</TableCell>
                                        <TableCell className={classes.bordercolors} style={designSummaryGroupedHeaderStyle()}>{valueColumnLabel}</TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {groupedTable.map((ele, rowIndex) => (
                                        ele?.month !== 13 && (
                                            <>
                                                <TableRow key={rowIndex}>
                                                    <StyledTableCell
                                                        className={`${classes.bordercolors} ${classes.groupedStickyMonthCell}`}
                                                    >
                                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                                                            <span>{formatDesignSummaryMonthLabel(ele?.month, ele?.year)}</span>
                                                            {isGroupingMode && (
                                                                <Checkbox
                                                                    size="small"
                                                                    style={{ padding: 2, color: '#FFF2EB' }}
                                                                    onChange={(e) =>
                                                                        handleRowSelected(
                                                                            `${ele?.month}/${ele?.year}`,
                                                                            e.target.checked
                                                                        )
                                                                    }
                                                                />
                                                            )}
                                                        </div>
                                                    </StyledTableCell>


                                                    {/* Render details */}
                                                    {groupedTable[0]?.detailsArray?.map((detail, index) => {
                                                        const detailKey = Object.keys(detail)[0];
                                                        const detailData = ele.detailsArray.find(d => Object.keys(d)[0] === detailKey) || {};
                                                        return (
                                                            <React.Fragment key={index}>
                                                                <TableCell
                                                                    className={`${classes.bordercolors} ${classes.groupedDataCell}`}
                                                                    style={designSummaryClickableCellStyle(DESIGN_SUMMARY_TABLE.cellData)}
                                                                    onClick={(e) => dataBasedOnClick(e, ele, detailKey)}
                                                                >
                                                                    {(detailData[detailKey]?.count || 0).toFixed(2)}
                                                                </TableCell>
                                                                {isCallingDesk ? renderCallingDeskCell('call', ele, detailKey, detailData[detailKey]?.count) : (
                                                                <TableCell
                                                                    className={`${classes.bordercolors} ${classes.groupedDataCell}`}
                                                                    style={designSummaryClickableCellStyle(DESIGN_SUMMARY_TABLE.cellData)}
                                                                    onClick={(e) => dataBasedOnClick(e, ele, detailKey)}
                                                                >
                                                                    {(detailData[detailKey]?.value || 0).toFixed(2)}
                                                                </TableCell>
                                                                )}
                                                            </React.Fragment>
                                                        );
                                                    })}
                                                    <TableCell className={`${classes.bordercolors} ${classes.groupedDataCell}`} style={designSummaryClickableCellStyle(DESIGN_SUMMARY_TABLE.cellGrandTotalColumn)} onClick={(e) => dataBasedOnClick(e, ele, "total")}>
                                                        {(ele?.totalCount || 0).toFixed(2)}
                                                    </TableCell>
                                                    {isCallingDesk ? renderCallingDeskCell('total-call', ele, "total", ele?.totalCount) : (
                                                    <TableCell className={`${classes.bordercolors} ${classes.groupedDataCell}`} style={designSummaryClickableCellStyle(DESIGN_SUMMARY_TABLE.cellGrandTotalColumn)} onClick={(e) => dataBasedOnClick(e, ele, "total")}>
                                                        {(ele?.grandTotal || 0).toFixed(2)}
                                                    </TableCell>
                                                    )}
                                                </TableRow>
                                            </>)))}

                                    {/* Grand Total footer row */}
                                    {groupedTable.length > 0 && (
                                        <TableRow className={classes.groupedFooterRow}>
                                            <StyledTableCell
                                                className={`${classes.bordercolors} ${classes.groupedStickyMonthCell} ${classes.groupedStickyMonthCellGrandTotal}`}
                                            >
                                                Grand Total
                                            </StyledTableCell>
                                            {groupedTable[0]?.detailsArray?.map((detail, index) => {
                                                const detailKey = Object.keys(detail)[0];
                                                // Calculate total from regular data only (not from storeTotalsRow to avoid duplication)
                                                const totalCount = groupedTable.reduce((acc, ele) => acc + (ele.detailsArray.find(d => Object.keys(d)[0] === detailKey)?.[detailKey]?.count || 0), 0);
                                                const totalValue = groupedTable.reduce((acc, ele) => acc + (ele.detailsArray.find(d => Object.keys(d)[0] === detailKey)?.[detailKey]?.value || 0), 0);

                                                const handleClick = (e) => {
                                                    const obj = {
                                                        year: 'Grand Total',
                                                        month: 'Total',
                                                        status: detailKey
                                                    };
                                                    dataBasedOnClick(e, obj, detailKey);
                                                };

                                                return (
                                                    <React.Fragment key={index}>
                                                        <TableCell
                                                            className={classes.bordercolors}
                                                            style={designSummaryClickableCellStyle(DESIGN_SUMMARY_TABLE.cellDataGrandTotalRow)}
                                                            onClick={handleClick}
                                                        >
                                                            {totalCount.toFixed(2)}
                                                        </TableCell>
                                                        {isCallingDesk ? renderCallingDeskCell('call', { year: 'Grand Total', month: 'Total', status: detailKey }, detailKey, totalCount) : (
                                                        <TableCell
                                                            className={classes.bordercolors}
                                                            style={designSummaryClickableCellStyle(DESIGN_SUMMARY_TABLE.cellDataGrandTotalRow)}
                                                            onClick={handleClick}
                                                        >
                                                            {totalValue.toFixed(2)}
                                                        </TableCell>
                                                        )}
                                                    </React.Fragment>
                                                );
                                            })}

                                            <TableCell
                                                className={classes.bordercolors}
                                                style={designSummaryClickableCellStyle(DESIGN_SUMMARY_TABLE.cellGrandTotalColumn)}
                                                onClick={() => {
                                                    const obj = { year: 'Grand Total', month: 'Total', status: 'total' };
                                                    dataBasedOnClick(null, obj, 'total');
                                                }}
                                            >
                                                {groupedTable.reduce((acc, ele) => acc + (ele?.totalCount || 0), 0).toFixed(2)}
                                            </TableCell>

                                            {isCallingDesk ? renderCallingDeskCell('total-call', { year: 'Grand Total', month: 'Total', status: 'total' }, 'total', groupedTable.reduce((acc, ele) => acc + (ele?.totalCount || 0), 0)) : (
                                            <TableCell
                                                className={classes.bordercolors}
                                                style={designSummaryClickableCellStyle(DESIGN_SUMMARY_TABLE.cellGrandTotalColumn)}
                                                onClick={() => {
                                                    const obj = { year: 'Grand Total', month: 'Total', status: 'total' };
                                                    dataBasedOnClick(null, obj, 'total');
                                                }}
                                            >
                                                {groupedTable.reduce((acc, ele) => acc + (ele?.grandTotal || 0), 0).toFixed(2)}
                                            </TableCell>
                                            )}
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </div>



                }

            </div>
            <Modal
                title= "Overall Summary"
                centered
                visible={showDetailedModal}
                onCancel={() => setShowDetailedModal(false)}
                width={1000}
                maskClosable={true}
                footer={null}
            >
                <div className={classes.csv}>
                    <CSVLink
                        headers={headers}
                        filename={`${pivot}.csv`}
                        data={csvData}
                        ref={csvButtonRef}
                    />

                    <Button
                        onClick={() => csvDownloadHandler(popMonthData)}
                        style={{ backgroundColor: "#355025", color: "#FFF2EB", marginRight: '10px' }}
                        variant="contained"
                        color="secondary"
                    >
                        Download CSV
                    </Button>

                </div>
                {popMonthData && (
                    <TableContainer component={Paper} className={classes.table}>
                        <Table
                            style={{ width: 450 }}
                            className={classes.table}
                            stickyHeader
                            aria-label="sticky table"
                        >
                            <TableHead>
                                <StyledTableRow>
                                    {isCallingDesk && <StyledTableCell style={{ textAlign: "center" }}>Call</StyledTableCell>}
                                    <StyledTableCell>Sl. No.</StyledTableCell>
                                    <StyledTableCell>Project No.</StyledTableCell>
                                    <StyledTableCell>Lead No.</StyledTableCell>
                                    <StyledTableCell>Client Name</StyledTableCell>
                                    <StyledTableCell>Current Stage</StyledTableCell>
                                    <StyledTableCell>Value (INR)</StyledTableCell>
                                    <StyledTableCell>Sales Won Value (INR)</StyledTableCell>
                                    <StyledTableCell>Design Won Value (INR)</StyledTableCell>
                                    <StyledTableCell>City</StyledTableCell>
                                    <StyledTableCell>Exp. Ctr. Name</StyledTableCell>
                                    <StyledTableCell>Design Experience Center</StyledTableCell>                             
                                    <StyledTableCell>Sales Won Date</StyledTableCell>
                                    <StyledTableCell>KOM Rejected Date</StyledTableCell>
                                    <StyledTableCell>DNA Pending With Design Date</StyledTableCell>
                                    <StyledTableCell>KickOff Internal Date</StyledTableCell>
                                    <StyledTableCell>KickOff Customer Date</StyledTableCell>
                                    <StyledTableCell>Design Sign-Off Date</StyledTableCell>
                                    <StyledTableCell>Material Delivery Date</StyledTableCell>
                                    <StyledTableCell>Actual Completion Date</StyledTableCell>
                                    <StyledTableCell>Cx Checklist Approval Date</StyledTableCell>
                                    <StyledTableCell> Designer DKO Checklist Approval TO ODM request Date</StyledTableCell>
                                     <StyledTableCell>DKO Checklist. - ODM Approval date</StyledTableCell>
                             
                                    {(status === "design-Hold" || status === "total") && 
                                        <>
                                            <StyledTableCell>Design-Hold Status</StyledTableCell>
                                            <StyledTableCell>Design-Hold Customer Approval</StyledTableCell>
                                        </>
                                    }    
                                    {(status === "completed" || status === "total") && <StyledTableCell>Overall Design NPS form/Feedback</StyledTableCell>}                                
                                    <StyledTableCell>Latest Notes/Date</StyledTableCell>
                                    <StyledTableCell>Latest Deal Activity</StyledTableCell>
                                    {(status === "lost" || status === "total") && <StyledTableCell>Lost Reason/Date</StyledTableCell>}
                                </StyledTableRow>
                            </TableHead>
                            <TableBody>
                                {popMonthData?.map((data, i) => {
                                    return (
                                        <StyledTableRow key={i}>
                                            {isCallingDesk && (
                                                <StyledTableCell component="th" style={{ textAlign: "center" }}>
                                                    <CallingDeskCallButton
                                                        disabled={!data?.leadId?._id}
                                                        title="Call this client"
                                                        onClick={() => openCallingDeskForLead(data)}
                                                    />
                                                </StyledTableCell>
                                            )}
                                            <StyledTableCell component="th">{i + 1}</StyledTableCell>
                                            <StyledTableCell
                                                component="th"
                                                style={{ textAlign: "center" }}
                                            >
                                                {/* {data?.leadId?.erpProjectNo || "N/A"} */}

                                                {
                                                    data?.leadId?.erpProjectNo ?
                                                        <Link to={`/project-logs/${data?.leadId?._id}`} target="_blank" rel="noopener noreferrer" style={{ color: "#000", textDecoration: 'underline', textAlign: 'center' }}>  {data?.leadId?.erpProjectNo}</Link> :
                                                        'NA'
                                                }
                                                {renderLeadReminderActions(data?.leadId)}

                                            </StyledTableCell>
                                            <StyledTableCell
                                                component="th"
                                                style={{ textAlign: "center" }}
                                            >
                                                {/* {data?.leadId?.lead_no} */}

                                                {
                                                    data?.leadId?.lead_no ?
                                                        <Link to={`/project-logs/${data?.leadId?._id}`} target="_blank" rel="noopener noreferrer" style={{ color: "#000", textDecoration: 'underline', textAlign: 'center' }}>  {data?.leadId?.lead_no}</Link> :
                                                        'NA'
                                                }

                                            </StyledTableCell>
                                            <StyledTableCell
                                                component="th"
                                                style={{ textAlign: "center" }}
                                            >
                                                {data?.leadId?.customerId?.name}
                                            </StyledTableCell>
                                            <StyledTableCell  component="th" style={{ textAlign: "center" }}>{data?.leadId?.currentStage}</StyledTableCell>
                                            {
                                                <>
                                                    <StyledTableCell component="th" style={{ textAlign: "center" }}>
                                                        {status === "completed"
                                                            ? ((data?.leadId?.staticDesignWonAmount / 1.18) / 100000)?.toLocaleString("en-IN", {
                                                                maximumFractionDigits: 2, // Maximum number of decimal places
                                                                style: "currency",
                                                                currency: "INR",
                                                            })
                                                            : data?.leadId?.staticSalesWonBudget?.toLocaleString("en-IN", {
                                                                maximumFractionDigits: 2, // Maximum number of decimal places
                                                                style: "currency",
                                                                currency: "INR",
                                                            })
                                                        }
                                                    </StyledTableCell>
                                                </>
                                            }
                                                   {
                                                <StyledTableCell component="th" style={{ textAlign: "center" }}>
                                                    {data?.leadId?.staticSalesWonBudget
                                                        ? data?.leadId?.staticSalesWonBudget?.toLocaleString("en-IN", {
                                                            maximumFractionDigits: 2, // Maximum number of decimal places
                                                            style: "currency",
                                                            currency: "INR",
                                                        })
                                                        : "NA"
                                                    }
                                                </StyledTableCell>

                                            }

                                            {<StyledTableCell component="th" style={{ textAlign: "center" }}>
                                                {data?.leadId?.staticDesignWonBudget
                                                    ? data?.leadId?.staticDesignWonBudget?.toLocaleString("en-IN", {
                                                        maximumFractionDigits: 2, // Maximum number of decimal places
                                                        style: "currency",
                                                        currency: "INR",
                                                    })
                                                    : "NA"
                                                }
                                            </StyledTableCell>}
                                            <StyledTableCell component="th" style={{ textAlign: "center" }}>
                                                {data?.salesExpCenter?.locationId?.name}
                                            </StyledTableCell>
                                            <StyledTableCell component="th" style={{ textAlign: "center" }}>
                                                {data?.salesExpCenter?.name}
                                            </StyledTableCell>
                                            <StyledTableCell component="th" style={{ textAlign: "center" }}>
                                                {data?.leadId?.designWonExpCtrId?.name}
                                            </StyledTableCell>
                                            <StyledTableCell component="th" style={{ textAlign: "center" }}>
                                                {data?.SalesWonDate
                                                    ? moment(data?.SalesWonDate).format("DD-MM-YYYY")
                                                    : "-"}
                                            </StyledTableCell>
                                            <StyledTableCell component="th" style={{ textAlign: "center" }}>
                                                {data?.designKOMRejectedStage
                                                    ? moment(data?.designKOMRejectedStage).format("DD-MM-YYYY")
                                                    : "-"}
                                            </StyledTableCell>
                                            <StyledTableCell component="th" style={{ textAlign: "center" }}>
                                                {data?.designerPendingAssignmentStage
                                                    ? moment(data?.designerPendingAssignmentStage).format("DD-MM-YYYY")
                                                    : "-"}
                                            </StyledTableCell>
                                            <StyledTableCell component="th" style={{ textAlign: "center" }}>
                                                {data?.kickoffInternalStage
                                                    ? moment(data?.kickoffInternalStage).format("DD-MM-YYYY")
                                                    : "-"}
                                            </StyledTableCell>
                                            <StyledTableCell component="th" style={{ textAlign: "center" }}>
                                                {data?.kickoffCustomerStage
                                                    ? moment(data?.kickoffCustomerStage).format("DD-MM-YYYY")
                                                    : "-"}
                                            </StyledTableCell>
                                            <StyledTableCell component="th" style={{ textAlign: "center" }}>
                                                {data?.leadId?.customerDesignSignOffDate
                                                    ? moment(data?.leadId?.customerDesignSignOffDate).format("DD-MM-YYYY")
                                                    : "-"}
                                            </StyledTableCell>
                                            <StyledTableCell component="th" style={{ textAlign: "center" }}>
                                                {data?.leadId?.materialDispatchedDate
                                                    ? moment(data?.leadId?.materialDispatchedDate).format(
                                                        "DD-MM-YYYY"
                                                    )
                                                    : "-"}
                                            </StyledTableCell>
                                            <StyledTableCell component="th" style={{ textAlign: "center" }}>
                                                {data?.leadId?.executionCompletionDate
                                                    ? moment(data?.leadId?.executionCompletionDate).format(
                                                        "DD-MM-YYYY"
                                                    )
                                                    : "-"}
                                            </StyledTableCell>
                                            <StyledTableCell component="th">
                                                {data?.leadId?.customerApproved[0]?.status === "Approved"
                                                    ? (data?.leadId?.customerApproved[0]?.approvedDate !== null
                                                        ? moment(data?.leadId?.customerApproved[0]?.approvedDate).format("DD-MM-YYYY")
                                                        : "NA")
                                                    : "NA"}
                                            </StyledTableCell>
                                            <StyledTableCell component="th">
                                                {data?.leadId?.agreementGeneratedDate ? moment(data?.leadId?.agreementGeneratedDate).format(
                                                    "DD-MM-YYYY"
                                                ) : "NA"}
                                            </StyledTableCell>
                                              <StyledTableCell component="th">
                                                {data?.leadId?.cxDKOApprovalDate ? moment(data?.leadId?.cxDKOApprovalDate).format(
                                                    "DD-MM-YYYY"
                                                ) : "NA"}
                                            </StyledTableCell>
                                             { (status === "design-Hold" || status === "total") && 
                                                <>
                                                    <StyledTableCell component="th" style={{ textAlign: "center" }}>{data?.leadId?.designStatus || 'NA'}</StyledTableCell>
                                                    <StyledTableCell component="th" style={{ textAlign: "center" }}>{data?.leadId?.designHoldStatusApproval?.customerApproval || 'NA'}</StyledTableCell>
                                                </> 
                                             }
                                            {(status === "completed" || status === "total") &&
                                                <StyledTableCell component="th" style={{ textAlign: "center" }}> <InfoIcon style={{ cursor: 'pointer', color: "#666" }} onClick={() => showNpsData(data)} /></StyledTableCell>
                                            }
                                                <StyledTableCell component="th" style={{ textAlign: "center" }}>
                                                    <InfoIcon style={{ cursor: 'pointer', color: "#666"}} onClick={() => showNotesInfo(data)} />
                                                </StyledTableCell>
                                                <StyledTableCell component="th" style={{ textAlign: "center" }}>
                                                    <InfoIcon style={{ cursor: 'pointer', color: "#666"}} onClick={() => setLatestDealData(data)} />
                                                </StyledTableCell>
                                            {(status === 'lost' || status === "total") &&
                                                <StyledTableCell component="th" style={{ textAlign: "center" }}>
                                                    {data?.stage === 'Lost' ? 
                                                        <InfoIcon style={{ cursor: 'pointer', color: "#666" }} onClick={() => showLostInfo(data)} />
                                                        :"NA"
                                                     }
                                                </StyledTableCell>
                                            }
                                        </StyledTableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Modal>

            {isCallingDesk && callingDeskTarget && (
                <CallingDeskDialog
                    onClose={() => setCallingDeskTarget(null)}
                    leadIds={callingDeskTarget.leadIds}
                    confirm={callingDeskTarget.confirm}
                    subtitle={callingDeskTarget.subtitle}
                    source="calling-desk"
                />
            )}

            {/* show latest notes modal */}
            <Dialog 
                open={showLatestNotes} 
                onClose={() => setShowLatestNotes(false)} 
                aria-labelledby="form-dialog-title" 
                fullWidth 
                maxWidth='sm'
                >
                <DialogTitle id="form-dialog-title">
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span>Latest Notes</span>
                    <span style={{ fontSize: "0.8rem", color: "#666" }}>
                        {latestNotesDate ? moment(latestNotesDate).format('lll') : ""}
                    </span>
                    </div>
                </DialogTitle>
                <Divider/>

                <DialogContent style={{ marginBottom: "18px" }}>
                    {latestNotesMsg || "No Notes"}
                </DialogContent>
            </Dialog>

            {/* latest Deal activity dialog */}
             <Dialog 
                open={latestDealModal} 
                onClose={() => setLatestDealModal(false)} 
                aria-labelledby="form-dialog-title" 
                fullWidth 
                maxWidth='sm'
                >
                <DialogTitle id="form-dialog-title">
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span>Latest Deal Activity</span>
                    <span style={{ fontSize: "0.8rem", color: "#666" }}>
                        {latestDealDate ? moment(latestDealDate).format('lll') : ""}
                    </span>
                    </div>
                </DialogTitle>
                <Divider/>

                <DialogContent style={{ marginBottom: "18px" }}>
                    {latestDealMsg || "No Notes"}
                </DialogContent>
            </Dialog>

            {/* lost Data dialog */}
             <Dialog 
                open={showLostNotes} 
                onClose={() => setShowLostNotes(false)} 
                aria-labelledby="form-dialog-title" 
                fullWidth 
                maxWidth='sm'
                >
                <DialogTitle id="form-dialog-title">
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span>Lost Activity</span>
                    <span style={{ fontSize: "0.8rem", color: "#666" }}>
                        {lostDealActivityDate ? moment(lostDealActivityDate).format('lll') : ""}
                    </span>
                    </div>
                </DialogTitle>
                <Divider/>

                <DialogContent style={{ marginBottom: "18px" }}>
                    {lostDealActivity || "NA"}
                </DialogContent>
            </Dialog>

            {/* NPS feedback and form */}
            <Dialog
                open={showNpsFormModal}
                onClose={() => setShowNpsFormModal(false)}
                aria-labelledby="form-dialog-title"
                fullWidth
                maxWidth='sm'
            >
                <DialogTitle id="form-dialog-title">
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span>Design Overall NPS Form/Feedback</span>
                        <span style={{ fontSize: "0.8rem", color: "#666" }}>
                            {npsCreatedDate ? moment(npsCreatedDate).format('lll') : ""}
                        </span>
                    </div>
                </DialogTitle>
                <Divider />
                <DialogContent style={{ marginBottom: "18px" }}>
                    <FormControl component="fieldset" style={{ marginBottom: 50 }}>
                        <FormLabel component="legend" style={{ color: "#000" }}>Satisfaction index - {avgScore}</FormLabel>
                        <FormLabel component="legend" style={{ color: "#000", marginTop: "30px" }}> <span className='satisfiedDetail'> </span> <span style={{ marginLeft: "10px", marginTop: "70px" }}>Dissatisfied </span>
                            <span className='DissatisfiedDetail'></span>
                            <span style={{ marginLeft: "10px", marginTop: "70px" }}> Satisfied</span> </FormLabel>
                    </FormControl>

                    <div className="designedDesktop">
                        <Table bordered responsive className="custom-table">
                            <thead>
                                <tr>
                                    {/* description header spans same 10 cells as body */}
                                    <th colSpan={10}></th>
                                    {[1, 2, 3, 4, 5].map((num) => (
                                        <th
                                            key={num}
                                            style={{
                                                backgroundColor: getColor(num),
                                                color: '#fff',
                                                textAlign: 'center',
                                            }}
                                        >
                                            {num}
                                        </th>
                                    ))}
                                </tr>
                            </thead>

                            <tbody>
                                {Array.isArray(npsFormData) && npsFormData.length > 0 ? (
                                    npsFormData
                                        .map((row, i) => (
                                            <tr key={i}>
                                                <td colSpan={10}>{row?.description}</td>

                                                {[1, 2, 3, 4, 5].map((num) => (
                                                    <td key={num} style={{ textAlign: 'center' }}>
                                                        <Radio
                                                            value={String(num)}
                                                            checked={row?.actionSelected === String(num)}
                                                            style={{
                                                                backgroundColor:
                                                                    row?.actionSelected === String(num) ? '#fde6f0' : '',
                                                            }}
                                                        />
                                                    </td>
                                                ))}
                                            </tr>
                                        ))
                                ) : (
                                    <tr>
                                        <td colSpan={15} style={{ textAlign: 'center' }}>
                                            No NPS Data
                                        </td>
                                    </tr>
                                )}

                            </tbody>
                        </Table>
                    </div>

                    <FormControl component="fieldset" style={{ marginBottom: 20, marginTop: 20 }}>
                        <FormLabel component="legend" style={{ color: "#000" }}>Was the Design Manager involved in your interior design journey? * </FormLabel>
                        <RadioGroup aria-label="designManagerInvolved" name="isDesignManagerInvolved">
                            <FormControlLabel value="Yes" control={<Radio color="primary" />} label="Yes"  checked = {isManagerInvolved === 'Yes'}/>
                            <FormControlLabel value="No" control={<Radio color="primary" />} label="No" checked = {isManagerInvolved === 'No'}/>
                            <FormControlLabel value="Not-applicable" control={<Radio color="primary" checked = {isManagerInvolved === 'Not-applicable'} />} label="Not applicable" />
                        </RadioGroup>
                    </FormControl>

                    {isManagerInvolved === 'Yes' && <>
                        <FormControl component="fieldset" style={{ marginBottom: 50 }}>
                            <FormLabel component="legend" style={{ color: "#000" }}>Satisfaction index -</FormLabel>
                            <FormLabel component="legend" style={{ color: "#000", marginTop: "30px" }}> <span className='satisfiedDetail'> </span> <span style={{ marginLeft: "10px", marginTop: "70px" }}>Dissatisfied </span>
                                <span className='DissatisfiedDetail'></span>
                                <span style={{ marginLeft: "10px", marginTop: "70px" }}> Satisfied</span> </FormLabel>
                        </FormControl>

                        <div className="designedDesktop">
                            <Table bordered responsive className="custom-table">
                                <thead>
                                    <tr>
                                        <th colSpan={10}></th>
                                        {[1, 2, 3, 4, 5].map((num) => (
                                            <th
                                                key={num}
                                                style={{
                                                    backgroundColor: getColor(num),
                                                    color: '#fff',
                                                    textAlign: 'center',
                                                }}
                                            >
                                                {num}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>

                                <tbody>
                                    {Array.isArray(managerSatisfactionForm) && managerSatisfactionForm?.length > 0 ? (
                                        managerSatisfactionForm
                                            ?.map((row, i) => (
                                                <tr key={i}>
                                                    <td colSpan={10}>{row?.description}</td>

                                                    {[1, 2, 3, 4, 5].map((num) => (
                                                        <td key={num} style={{ textAlign: 'center' }}>
                                                            <Radio
                                                                value={String(num)}
                                                                checked={row?.actionSelected === String(num)}
                                                                style={{
                                                                    backgroundColor:
                                                                        row?.actionSelected === String(num) ? '#fde6f0' : '',
                                                                }}
                                                            />
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))
                                    ) : (
                                        <tr>
                                            <td colSpan={15} style={{ textAlign: 'center' }}>
                                                No NPS Data
                                            </td>
                                        </tr>
                                    )}

                                </tbody>
                            </Table>
                        </div>
                    </>
                    }
                    <Divider/>
                    <b>Feedback: </b> {npsFeedback || "NA"}
                </DialogContent>
                 <DialogActions>
                        <Button onClick={() => setShowNpsFormModal(false)} color="primary" variant="contained">
                            Cancel
                        </Button>
                 </DialogActions>
            </Dialog>
        </div >
    );
}

export default connect(null, { setLoading, setSnackInfo })(DesignSummary);
